import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Fade,
    Tooltip,
    Stack,
    Chip,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import {
    UploadFile as UploadFileIcon,
    Search as SearchIcon,
    AutoAwesome as AutoAwesomeIcon,
    BusinessCenter as BusinessCenterIcon,
    GridView as GridViewIcon,
    ViewList as ViewListIcon,
    FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";

// API layer
import { jdApi } from "../../api/jobdescriptionApi";
import { candidateApi } from "../../api/candidateApi";
import {
    ERROR_MESSAGES,
    getPlanExhaustMessage,
} from "../../components/common/StatusDictionary";
import FileUploadModal from "../FileUploadModal";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomPagination from "../../components/common/CustomPagination";
import CardPagination from "../../components/common/CardPagination";

// Local components
import JobCard, { useTooltipProps } from "../../components/RecruiterLanding/JobCard";
import JobTable from "../../components/RecruiterLanding/JobTable";
import SkillsModal from "../../components/RecruiterLanding/SkillsModal";

import QuizIcon from "@mui/icons-material/Quiz";

/* ------------------------------------------------------------------
   STATUS ORDERING — fixed/static order regardless of API response
   order. Lower number = shown first.
   NOTE: adjust the keys on the left if your backend uses different
   status strings (e.g. "ACTIVE" instead of "OPEN"). Everything is
   normalized to lowercase + stripped of spaces/underscores/hyphens
   before lookup, so "On Hold", "on_hold", "ONHOLD" all match.
------------------------------------------------------------------ */
const STATUS_ORDER = {
    open: 1,
    active: 1,
    onhold: 2,
    hold: 2,
    paused: 2,
    closed: 3,
    inactive: 3,
    completed: 3,
};

const STATUS_FILTER_OPTIONS = [
    { value: "all", label: "All Statuses" },
    { value: "open", label: "Open" },
    { value: "onhold", label: "Onhold" },
    { value: "closed", label: "Closed" },
];

const normalizeStatus = (status) =>
    (status || "").toString().toLowerCase().replace(/[\s_-]/g, "");

const getStatusRank = (status) => {
    const key = normalizeStatus(status);
    return STATUS_ORDER[key] ?? 99; // unknown statuses sink to the bottom
};

/* ------------------------------------------------------------------
   SKELETON GRID — shown while data is loading
------------------------------------------------------------------ */
const SkeletonGrid = () => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    xl: "repeat(4, 1fr)",
                },
                gap: 2.2,
            }}
        >
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <Box
                    key={i}
                    sx={{
                        height: 180,
                        borderRadius: "16px",
                        border: `1px solid ${theme.palette.divider}`,
                        background: `linear-gradient(90deg,
                            ${alpha(theme.palette.text.primary, 0.03)} 25%,
                            ${alpha(theme.palette.text.primary, 0.08)} 50%,
                            ${alpha(theme.palette.text.primary, 0.03)} 75%)`,
                        backgroundSize: "1000px 100%",
                        animation: "shimmer 2s infinite linear",
                        "@keyframes shimmer": {
                            "0%": { backgroundPosition: "-1000px 0" },
                            "100%": { backgroundPosition: "1000px 0" },
                        },
                    }}
                />
            ))}
        </Box>
    );
};

/* ------------------------------------------------------------------
   RECRUITER LANDING — thin orchestrator
------------------------------------------------------------------ */
const RecruiterLanding = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const hasFetchedRef = useRef(false);
    const ttProps = useTooltipProps(theme);

    // ── Data ──────────────────────────────────────────────────────
    const [cardData, setCardData] = useState([]);
    const allCardDataRef = useRef([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

    // ── Infinite Scroll Card Data ───────────────────────────────
    const [scrollData, setScrollData] = useState([]);
    const [scrollPage, setScrollPage] = useState(1);
    const [scrollHasMore, setScrollHasMore] = useState(true);
    const [scrollLoading, setScrollLoading] = useState(false);
    const allScrollDataRef = useRef([]);

    // ── Pagination ────────────────────────────────────────────────
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // ── Modal visibility ──────────────────────────────────────────
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [applyJob, setApplyJob] = useState(null);
    const [editSkillModal, setEditSkillModal] = useState(false);

    // ── Per-item loading trackers ─────────────────────────────────
    const [viewingJdId, setViewingJdId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openingSkillsId, setOpeningSkillsId] = useState(null);

    // ── Skills modal state ────────────────────────────────────────
    const [selectedJD, setSelectedJD] = useState(null);
    const [skillData, setSkillData] = useState(null);
    const [skillsLoading, setSkillsLoading] = useState(false);
    const [savingSkills, setSavingSkills] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // ── Global notification ───────────────────────────────────────
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const notify = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    // ── Fetch ─────────────────────────────────────────────────────
    const fetchJDs = useCallback(async (targetPage = 1, isScroll = false, shouldAppend = false) => {
        if (isScroll && shouldAppend) {
            setScrollLoading(true);
        } else {
            setLoading(true);
        }

        try {
            const size = isScroll ? 20 : rowsPerPage;
            const response = await jdApi.getRecruiterJDs({
                page: targetPage,
                page_size: size
            });

            const { status, data } = response || {};

            if (status === 200 && data.results) {
                const results = data.results || [];
                if (isScroll) {
                    setScrollData((prev) => shouldAppend ? [...prev, ...results] : results);
                    allScrollDataRef.current = shouldAppend ? [...allScrollDataRef.current, ...results] : results;

                    const pagination = data?.pagination || {};
                    setScrollHasMore(pagination.has_next || false);
                    setScrollPage(targetPage);
                    setTotal(pagination.total || 0);
                } else {
                    setCardData(results);
                    allCardDataRef.current = results;
                    setTotal(data?.pagination?.total || 0);
                    setTotalPages(data?.pagination?.total_pages || 1);
                }
            } else {
                if (isScroll) {
                    if (!shouldAppend) {
                        setScrollData([]);
                        allScrollDataRef.current = [];
                    }
                    setScrollHasMore(false);
                } else {
                    setCardData([]);
                    allCardDataRef.current = [];
                    setTotal(0);
                    setTotalPages(1);
                }
            }
        } catch (error) {
            console.error("Failed to fetch job descriptions:", error);
            notify("Failed to fetch job descriptions.", "error");
            if (isScroll) {
                if (!shouldAppend) {
                    setScrollData([]);
                    allScrollDataRef.current = [];
                }
                setScrollHasMore(false);
            } else {
                setCardData([]);
                allCardDataRef.current = [];
            }
        } finally {
            setScrollLoading(false);
            setLoading(false);
        }
    }, [rowsPerPage]);

    useEffect(() => {
        if (viewMode === "list") {
            fetchJDs(page, false, false);
        } else if (viewMode === "grid" && scrollData.length === 0) {
            fetchJDs(1, true, false);
        }
    }, [fetchJDs, page, scrollData.length, viewMode]);

    const reloadCurrentView = useCallback(() => {
        if (viewMode === "list") {
            fetchJDs(page, false, false);
        } else {
            fetchJDs(1, true, false);
        }
    }, [fetchJDs, page, viewMode]);

    // ── Handlers ──────────────────────────────────────────────────
    const handleView = async (item) => {
        if (viewingJdId) return;
        setViewingJdId(item.id);
        try {
            const response = await jdApi.viewJD({ jd_id: item.id });
            const url = response?.data?.download_url;
            if (url) window.open(url, "_blank");
        } catch {
            notify("Failed to open JD", "error");
        } finally {
            setViewingJdId(null);
        }
    };

    const handleOnStatusChange = async (item, status) => {
        try {
            const response = await jdApi.statusUpdate(item.id, { status });

            if (response?.status) {
                notify(response?.message || "Status updated successfully", "info");

                // Optimistically update local state so the item re-sorts
                // into its correct status bucket immediately, instead of
                // waiting on (and being at the mercy of) server order.
                setCardData((prev) =>
                    prev.map((jd) => (jd.id === item.id ? { ...jd, status } : jd))
                );
                setScrollData((prev) =>
                    prev.map((jd) => (jd.id === item.id ? { ...jd, status } : jd))
                );

                reloadCurrentView();
            } else {
                notify(response?.message || "Failed to update status", "error");
            }

            return response;
        } catch (error) {
            console.error("Status update failed:", error);
            notify(
                error?.response?.data?.message || "Failed to update status",
                "error"
            );
            throw error;
        }
    };

    const handleApply = (item) => setApplyJob(item);

    const handleMatch = (item) =>
        navigate("/recruiter/llm-matching", { state: { jdId: item.id } });

    const handleSkills = async (item) => {
        setOpeningSkillsId(item.id);
        setSelectedJD(item);
        setSkillData(null);
        setEditSkillModal(true);
        setSkillsLoading(true);
        setSaveSuccess(false);
        try {
            const response = await jdApi.getRecruiterJDDetail(item.id);
            if (response?.status === 200) setSkillData(response.data);
            else {
                notify("Failed to fetch JD details", "error");
                setEditSkillModal(false);
            }
        } catch {
            notify("Failed to load skills", "error");
            setEditSkillModal(false);
        } finally {
            setSkillsLoading(false);
            setOpeningSkillsId(null);
        }
    };

    const handleSaveSkills = async () => {
        setSavingSkills(true);
        setSaveSuccess(false);
        try {
            const payload = {
                skill: {
                    ...skillData.skill,
                    keySkills: Array.isArray(skillData.skill.keySkills)
                        ? skillData.skill.keySkills.join(", ")
                        : skillData.skill.keySkills,
                    additionalSkills: Array.isArray(skillData.skill.additionalSkills)
                        ? skillData.skill.additionalSkills.join(", ")
                        : skillData.skill.additionalSkills,
                },
            };
            const response = await jdApi.updateRecruiterJDDetail(
                selectedJD.id,
                payload
            );
            if (response?.status === 200) {
                setSaveSuccess(true);
                setTimeout(() => {
                    notify("Skills updated successfully");
                    reloadCurrentView();
                    setEditSkillModal(false);
                    setSaveSuccess(false);
                }, 800);
            } else {
                notify(response?.message || "Failed to update skills", "error");
            }
        } catch {
            notify("Failed to update skills", "error");
        } finally {
            setSavingSkills(false);
        }
    };

    const handleUploadSuccess = (data) => {
        const id = data?.data?.data?.id;
        if (id) {
            navigate("/recruiter/analysis", { state: { id } });
        } else {
            notify("JD uploaded successfully!");
            reloadCurrentView();
        }
    };

    const handleApplySuccess = () => {
        setApplyJob(null);
        notify("Resume uploaded successfully!");
        reloadCurrentView();
    };

    const handleUploadError = (error) => {
        // Extract message gracefully, accounting for Axios responses
        const serverMessage = error?.response?.data?.message || error?.message;
        notify(serverMessage, error?.severity || "error");
    };

    // ── Derived ───────────────────────────────────────────────────
    // 1) filter by search text + status dropdown
    // 2) sort into a FIXED order: Open -> Onhold -> Closed -> (anything else)
    //    Within the same status bucket, alphabetical order A-Z by job_title
    const filteredData = useMemo(() => {
        const source = viewMode === "list" ? cardData : scrollData;

        let result = source;

        if (searchQuery) {
            const lower = searchQuery.toLowerCase();
            result = result.filter((item) =>
                item.job_title?.toLowerCase().includes(lower) ||
                item.requisition_code?.toLowerCase().includes(lower)
            );
        }

        if (statusFilter !== "all") {
            result = result.filter(
                (item) => normalizeStatus(item.status) === statusFilter
            );
        }

        // Sort by fixed status rank, then by job_title alphabetically (A-Z)
        return result
            .map((item, index) => ({ item, index }))
            .sort((a, b) => {
                const rankDiff = getStatusRank(a.item.status) - getStatusRank(b.item.status);
                
                // If statuses are different, sort by status rank
                if (rankDiff !== 0) return rankDiff;

                // If statuses are the same, sort alphabetically by job_title
                const titleA = (a.item.job_title || "").toLowerCase();
                const titleB = (b.item.job_title || "").toLowerCase();
                
                if (titleA < titleB) return -1;
                if (titleA > titleB) return 1;

                // If titles are also identical, preserve original order
                return a.index - b.index;
            })
            .map(({ item }) => item);
    }, [cardData, scrollData, searchQuery, statusFilter, viewMode]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setRowsPerPage(newSize);
        setPage(1);
    };

    const handleLoadMoreScroll = () => {
        if (scrollHasMore && !scrollLoading) {
            fetchJDs(scrollPage + 1, true, true);
        }
    };

    const handleViewModeChange = (newMode) => {
        if (newMode === viewMode) return;
        setViewMode(newMode);
        searchQuery && setSearchQuery("");
        if (newMode === "list") {
            setPage(1);
        }
    };

    // ── Render ────────────────────────────────────────────────────
    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: theme.palette.background.default,
                p: { xs: 1.2, md: 2.5, lg: 3.5 },
                fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
        >
            {/* ── HEADER ── */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", md: "center" },
                    gap: 1.8,
                    mb: 3.5,
                }}
            >
                {/* Title */}
                <Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontSize: "1.55rem",
                            fontWeight: 800,
                            color: theme.palette.text.primary,
                            letterSpacing: "-0.02em",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                        }}
                    >
                        Workspace
                        <Tooltip
                            title={`${total} active job openings`}
                            placement="right"
                            {...ttProps}
                        >
                            <Chip
                                label={`${total} Openings`}
                                size="small"
                                sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main,
                                    fontWeight: 700,
                                    borderRadius: "6px",
                                    fontSize: "0.6rem",
                                    height: 20,
                                    cursor: "default",
                                }}
                            />
                        </Tooltip>
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: "0.72rem",
                            color: theme.palette.text.secondary,
                            mt: 0.5,
                            fontWeight: 500,
                        }}
                    >
                        Manage your job postings and track applicants seamlessly.
                    </Typography>
                </Box>

                {/* Controls */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        flexWrap: "wrap",
                    }}
                >
                    {/* Search */}
                    <Tooltip title="Search by job title" placement="bottom" {...ttProps}>
                        <TextField
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search positions..."
                            disabled={scrollData.length === 0}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontSize: 16,
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                minWidth: { xs: "100%", sm: 220 },
                                "& .MuiOutlinedInput-root": {
                                    height: 34,
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: "8px",
                                    fontSize: "0.72rem",
                                    fontWeight: 500,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    transition: "all 0.2s ease",
                                    boxShadow: isDark
                                        ? "none"
                                        : "0 2px 4px rgba(0,0,0,0.02)",
                                    "& fieldset": {
                                        borderColor: theme.palette.divider,
                                        borderWidth: "1px",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: alpha(theme.palette.text.primary, 0.3),
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: "2px",
                                    },
                                    "&.Mui-focused": {
                                        boxShadow: `0 4px 12px ${alpha(
                                            theme.palette.primary.main,
                                            0.1
                                        )}`,
                                    },
                                },
                            }}
                        />
                    </Tooltip>

                    {/* Status filter dropdown */}
                    {/* <Tooltip title="Filter by status" placement="bottom" {...ttProps}> */}
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: 150,
                                "& .MuiOutlinedInput-root": {
                                    height: 34,
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: "8px",
                                    fontSize: "0.72rem",
                                    fontWeight: 600,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    boxShadow: isDark
                                        ? "none"
                                        : "0 2px 4px rgba(0,0,0,0.02)",
                                    "& fieldset": {
                                        borderColor: theme.palette.divider,
                                        borderWidth: "1px",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: alpha(theme.palette.text.primary, 0.3),
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: "2px",
                                    },
                                },
                            }}
                        >
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                displayEmpty
                                startAdornment={
                                    <InputAdornment position="start">
                                        <FilterListIcon
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontSize: 16,
                                            }}
                                        />
                                    </InputAdornment>
                                }
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            "& .MuiMenuItem-root": {
                                                fontSize: "0.72rem",
                                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                            },
                                        },
                                    },
                                }}
                            >
                                {STATUS_FILTER_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    {/* </Tooltip> */}

                    {/* Grid / List toggle */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "8px",
                            overflow: "hidden",
                            bgcolor: theme.palette.background.paper,
                            boxShadow: isDark ? "none" : "0 2px 4px rgba(0,0,0,0.02)",
                        }}
                    >
                        <Tooltip title="Grid view" placement="bottom" {...ttProps}>
                            <IconButton
                                size="small"
                                onClick={() => handleViewModeChange("grid")}
                                sx={{
                                    borderRadius: 0,
                                    height: 34,
                                    width: 34,
                                    bgcolor:
                                        viewMode === "grid"
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : "transparent",
                                    color:
                                        viewMode === "grid"
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    "&:hover": {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                }}
                            >
                                <GridViewIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Box
                            sx={{
                                width: "1px",
                                height: 20,
                                bgcolor: theme.palette.divider,
                            }}
                        />
                        <Tooltip title="List view" placement="bottom" {...ttProps}>
                            <IconButton
                                size="small"
                                onClick={() => handleViewModeChange("list")}
                                sx={{
                                    borderRadius: 0,
                                    height: 34,
                                    width: 34,
                                    bgcolor:
                                        viewMode === "list"
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : "transparent",
                                    color:
                                        viewMode === "list"
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    "&:hover": {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                }}
                            >
                                <ViewListIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Upload */}
                    <Tooltip
                        title="Upload a job description PDF or DOCX"
                        placement="bottom"
                        {...ttProps}
                    >
                        <Button
                            onClick={() => setShowUploadModal(true)}
                            startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                            sx={{
                                height: 34,
                                px: 2,
                                borderRadius: "8px",
                                flex: { xs: 1, sm: "none" },
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                fontFamily: "inherit",
                                bgcolor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: isDark ? "none" : "0 2px 4px rgba(0,0,0,0.02)",
                                transition: "all 0.2s",
                                "&:hover": {
                                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                                    borderColor: alpha(theme.palette.text.primary, 0.3),
                                    transform: "translateY(-1px)",
                                },
                            }}
                        >
                            Upload
                        </Button>
                    </Tooltip>

                    {/* Generate */}
                    <Tooltip
                        title="Use AI to auto-generate a Job Description"
                        placement="bottom"
                        {...ttProps}
                    >
                        <Button
                            onClick={() => navigate("/recruiter/jd-generator")}
                            startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                            sx={{
                                height: 34,
                                px: 2,
                                borderRadius: "8px",
                                flex: { xs: 1, sm: "none" },
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                fontFamily: "inherit",
                                background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                                color: "#fff",
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                transition: "all 0.2s",
                                "&:hover": {
                                    transform: "translateY(-1px)",
                                    boxShadow: `0 6px 16px ${alpha(
                                        theme.palette.primary.main,
                                        0.4
                                    )}`,
                                },
                            }}
                        >
                            Generate
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {/* ── CONTENT AREA ── */}
            <Box>
                {loading ? (
                    <SkeletonGrid />
                ) : (
                    <Fade in={!loading} timeout={500} mountOnEnter unmountOnExit>
                        <Box>
                            {filteredData.length > 0 ? (
                                <>
                                    {/* GRID VIEW */}
                                    <Fade
                                        key="grid"
                                        in={viewMode === "grid"}
                                        timeout={250}
                                        mountOnEnter
                                        unmountOnExit
                                    >
                                        <Box sx={{ width: "100%" }}>
                                            <CardPagination
                                                hasMore={scrollHasMore}
                                                loading={scrollLoading}
                                                onLoadMore={handleLoadMoreScroll}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: {
                                                            xs: "1fr",
                                                            sm: "repeat(2, 1fr)",
                                                            md: "repeat(3, 1fr)",
                                                            xl: "repeat(4, 1fr)",
                                                        },
                                                        gap: 2.2,
                                                    }}
                                                >
                                                    {filteredData.map((item) => (
                                                        <JobCard
                                                            key={item.id}
                                                            item={item}
                                                            viewingJdId={viewingJdId}
                                                            openingSkillsId={openingSkillsId}
                                                            onStatusChange={handleOnStatusChange}
                                                            onView={handleView}
                                                            onApply={handleApply}
                                                            onMatch={handleMatch}
                                                            onSkills={handleSkills}
                                                            onQuestions={(item) =>
                                                                navigate("/recruiter/questions", {
                                                                    state: {
                                                                        jdId: item.id,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    ))}
                                                </Box>
                                            </CardPagination>
                                        </Box>
                                    </Fade>

                                    {/* LIST VIEW */}
                                    <Fade
                                        key="list"
                                        in={viewMode === "list"}
                                        timeout={250}
                                        mountOnEnter
                                        unmountOnExit
                                    >
                                        <Box>
                                            <JobTable
                                                data={filteredData}
                                                viewingJdId={viewingJdId}
                                                openingSkillsId={openingSkillsId}
                                                onView={handleView}
                                                onApply={handleApply}
                                                onMatch={handleMatch}
                                                onSkills={handleSkills}
                                            />
                                            <CustomPagination
                                                page={page}
                                                totalPages={totalPages}
                                                total={total}
                                                pageSize={rowsPerPage}
                                                onPageChange={handlePageChange}
                                                onPageSizeChange={handlePageSizeChange}
                                                pageSizeOptions={[5, 10, 25]}
                                                sx={{ mt: 2 }}
                                            />
                                        </Box>
                                    </Fade>
                                </>
                            ) : (
                                /* EMPTY STATE */
                                <Box
                                    sx={{
                                        maxWidth: 400,
                                        mx: "auto",
                                        textAlign: "center",
                                        borderRadius: "16px",
                                        border: `2px dashed ${theme.palette.divider}`,
                                        py: 5,
                                        bgcolor: "transparent",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: "12px",
                                            bgcolor: alpha(theme.palette.text.primary, 0.04),
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mx: "auto",
                                            mb: 2,
                                        }}
                                    >
                                        <BusinessCenterIcon
                                            sx={{
                                                fontSize: 24,
                                                color: theme.palette.text.secondary,
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontSize: "1rem",
                                            mb: 0.8,
                                            fontWeight: 800,
                                            color: theme.palette.text.primary,
                                            fontFamily: "inherit",
                                        }}
                                    >
                                        No Openings Found
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color={theme.palette.text.secondary}
                                        sx={{
                                            fontSize: "0.72rem",
                                            mb: 3,
                                            fontWeight: 500,
                                            fontFamily: "inherit",
                                        }}
                                    >
                                        {searchQuery || statusFilter !== "all"
                                            ? "Try adjusting your search or status filter."
                                            : "Get started by uploading your first Job Description."}
                                    </Typography>
                                    {!searchQuery && statusFilter === "all" && (
                                        <Button
                                            variant="contained"
                                            onClick={() => setShowUploadModal(true)}
                                            startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                                            sx={{
                                                borderRadius: "8px",
                                                fontSize: "0.72rem",
                                                textTransform: "none",
                                                fontWeight: 700,
                                                fontFamily: "inherit",
                                                px: 2,
                                                py: 0.8,
                                            }}
                                        >
                                            Upload First JD
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Fade>
                )}
            </Box>

            {/* ── MODALS ── */}

            {/* Apply (resume upload) */}
            {applyJob && (
                <FileUploadModal
                    open={Boolean(applyJob)}
                    uploadType="resume"
                    apiEndpoint={candidateApi.apply}
                    onClose={() => setApplyJob(null)}
                    setLoading={setIsSubmitting}
                    extraPayload={{
                        job_id: applyJob.id,
                        recruiter_id: applyJob.created_by,
                    }}
                    postUploadHandler={handleApplySuccess}
                    onError={handleUploadError}
                />
            )}

            {/* JD upload */}
            {showUploadModal && (
                <FileUploadModal
                    open={showUploadModal}
                    uploadType="jd"
                    apiEndpoint={jdApi.createRecruiterJD}
                    onClose={() => setShowUploadModal(false)}
                    setLoading={setLoading}
                    onSuccess={handleUploadSuccess}
                    onError={handleUploadError}
                />
            )}

            {/* Skills editor */}
            <SkillsModal
                open={editSkillModal}
                onClose={() => { if (!savingSkills) setEditSkillModal(false); }}
                onSave={handleSaveSkills}
                selectedJD={selectedJD}
                skillData={skillData}
                setSkillData={setSkillData}
                skillsLoading={skillsLoading}
                savingSkills={savingSkills}
                saveSuccess={saveSuccess}
            />

            {/* ── Global snackbar ── */}
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                autoHideDuration={4000}
            />
        </Box>
    );
};

export default RecruiterLanding;



//////////////////////seperatoor/////////////////////////////


////old code ///
// import React, { useState, useEffect, useRef } from "react";
// import {
//     Box,
//     Typography,
//     Grid,
//     Fade,
//     Tooltip,
//     Stack,
//     Chip,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     TextField,
//     Button,
//     Autocomplete,
//     CircularProgress,
// } from "@mui/material";
// import {
//     BusinessCenter as BusinessCenterIcon,
//     AutoFixHigh as AutoFixHighIcon,
//     LocationOn as LocationIcon,
//     CalendarToday as CalendarIcon,
//     UploadFile as UploadFileIcon,
//     PersonSearch as PersonSearchIcon,
//     HowToReg as HowToRegIcon,
//     PsychologyAlt as PsychologyAltIcon,
//     CheckCircle as CheckCircleIcon,
//     Close as CloseIcon,
//     WorkspacePremium as WorkspacePremiumIcon,
//     Add as AddIcon,
// } from "@mui/icons-material";
// import { useNavigate } from "react-router-dom";
// import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
// import IconButton from "@mui/material/IconButton";

// import { jdApi } from "../../api/jobdescriptionApi";
// import { ERROR_MESSAGES, getPlanExhaustMessage } from "../../components/common/StatusDictionary";
// import { candidateApi } from "../../api/candidateApi";

// import { formatDate, formatTime } from "../../utils/dateUtils";
// import FileUploadModal from "../FileUploadModal";
// import CustomSnackbar from "../../components/common/CustomSnackbar";
// import CustomCard from "../../components/common/CustomCard";
// import CustomButton from "../../components/common/CustomButton";
// import SearchBox from "../../components/common/CustomSearchBar";
// import CustomHeader from "../../components/common/CustomHeader";
// import SkeletonLoader from "../../components/common/SkeletonLoader";

// /* ─── Unique orbital loader ─────────────────────────────────────── */
// const jdLoaderStyles = `
// @keyframes jdOrbit {
//   0%   { transform: rotate(0deg)   translateX(6px) rotate(0deg); }
//   100% { transform: rotate(360deg) translateX(6px) rotate(-360deg); }
// }
// @keyframes jdPulseRing {
//   0%, 100% { opacity: 0.35; transform: scale(0.82); }
//   50%       { opacity: 1;    transform: scale(1); }
// }
// .jd-loader-root {
//   position: relative;
//   width: 16px;
//   height: 16px;
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
//   flex-shrink: 0;
// }
// .jd-loader-ring {
//   position: absolute;
//   inset: 0;
//   border-radius: 50%;
//   border: 1.5px solid currentColor;
//   opacity: 0.35;
//   animation: jdPulseRing 1.1s ease-in-out infinite;
// }
// .jd-loader-dot {
//   position: absolute;
//   width: 3.5px;
//   height: 3.5px;
//   border-radius: 50%;
//   background: currentColor;
//   animation: jdOrbit 0.85s linear infinite;
// }
// .jd-loader-dot:nth-of-type(3) { animation-delay: -0.28s; }
// .jd-loader-dot:nth-of-type(4) { animation-delay: -0.56s; }
// `;

// /* ─── Skills modal styles ──────────────────────────────────────── */
// const skillsModalStyles = `
// @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

// @keyframes skillFadeUp {
//   from { opacity: 0; transform: translateY(10px); }
//   to   { opacity: 1; transform: translateY(0); }
// }
// @keyframes skillPulse {
//   0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.3); }
//   50%       { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
// }
// @keyframes shimmer {
//   0%   { background-position: -400px 0; }
//   100% { background-position: 400px 0; }
// }
// @keyframes spinArc {
//   to { transform: rotate(360deg); }
// }
// @keyframes dotBounce {
//   0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
//   40%           { transform: scale(1);   opacity: 1; }
// }
// @keyframes skillChipIn {
//   from { opacity: 0; transform: scale(0.7) translateY(4px); }
//   to   { opacity: 1; transform: scale(1) translateY(0); }
// }
// @keyframes checkPop {
//   0%   { transform: scale(0) rotate(-10deg); opacity: 0; }
//   60%  { transform: scale(1.25) rotate(3deg); opacity: 1; }
//   100% { transform: scale(1) rotate(0deg); opacity: 1; }
// }
// @keyframes gradShift {
//   0%   { background-position: 0% 50%; }
//   50%  { background-position: 100% 50%; }
//   100% { background-position: 0% 50%; }
// }

// /* loading skeleton shimmer */
// .skills-shimmer-line {
//   border-radius: 8px;
//   background: linear-gradient(90deg, #f0f0f0 25%, #e0e4ff 50%, #f0f0f0 75%);
//   background-size: 400px 100%;
//   animation: shimmer 1.4s ease-in-out infinite;
// }

// /* save spinner */
// .skills-save-spinner {
//   width: 16px; height: 16px;
//   border: 2px solid rgba(255,255,255,0.35);
//   border-top-color: #fff;
//   border-radius: 50%;
//   animation: spinArc 0.7s linear infinite;
//   flex-shrink: 0;
// }

// /* loading dots */
// .skills-dot {
//   width: 6px; height: 6px;
//   border-radius: 50%;
//   background: #6366f1;
//   display: inline-block;
//   animation: dotBounce 1.2s ease-in-out infinite;
// }
// .skills-dot:nth-of-type(2) { animation-delay: 0.2s; }
// .skills-dot:nth-of-type(3) { animation-delay: 0.4s; }

// /* chip animation */
// .skills-chip-anim {
//   animation: skillChipIn 0.25s cubic-bezier(0.34,1.56,0.64,1) forwards;
// }

// /* check pop animation */
// .skills-check-anim {
//   animation: checkPop 0.45s cubic-bezier(0.34,1.56,0.64,1) forwards;
// }

// /* header gradient band */
// .skills-header-band {
//   background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
//   background-size: 200% 200%;
//   animation: gradShift 5s ease infinite;
// }

// /* MuiChip override inside skills dialog */
// .skills-dialog-chip .MuiChip-root {
//   font-family: 'DM Sans', sans-serif;
//   font-size: 0.75rem;
//   font-weight: 500;
//   border-radius: 20px;
//   height: 28px;
//   background: linear-gradient(135deg, #eef2ff, #ede9fe);
//   color: #4338ca;
//   border: 1px solid #c7d2fe;
//   transition: all 0.18s ease;
// }
// .skills-dialog-chip .MuiChip-root:hover {
//   background: linear-gradient(135deg, #e0e7ff, #ddd6fe);
//   box-shadow: 0 2px 8px rgba(99,102,241,0.2);
// }
// .skills-dialog-chip .MuiChip-deleteIcon {
//   color: #818cf8;
//   font-size: 14px;
// }
// .skills-dialog-chip .MuiChip-deleteIcon:hover {
//   color: #4338ca;
// }
// `;

// let jdStyleInjected = false;
// const injectStyles = () => {
//     if (!jdStyleInjected) {
//         const tag = document.createElement("style");
//         tag.textContent = jdLoaderStyles + skillsModalStyles;
//         document.head.appendChild(tag);
//         jdStyleInjected = true;
//     }
// };

// const JdLoader = ({ color = "inherit" }) => {
//     injectStyles();
//     return (
//         <span className="jd-loader-root" style={{ color }}>
//             <span className="jd-loader-ring" />
//             <span className="jd-loader-dot" />
//             <span className="jd-loader-dot" />
//         </span>
//     );
// };
// /* ──────────────────────────────────────────────────────────────── */

// /* ─── Skills Loading Overlay ────────────────────────────────────── */
// const SkillsLoadingState = () => (
//     <Box sx={{ px: 0.5, pt: 1, pb: 0.5 }}>
//         {/* shimmer lines mimicking label + chips */}
//         <Box sx={{ mb: 2.5 }}>
//             <Box className="skills-shimmer-line" sx={{ width: 80, height: 12, mb: 1.5 }} />
//             <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//                 {[90, 70, 110, 80, 95].map((w, i) => (
//                     <Box key={i} className="skills-shimmer-line" sx={{ width: w, height: 28, borderRadius: "20px" }} />
//                 ))}
//             </Box>
//         </Box>
//         {/* centered dots */}
//         <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.8, pt: 1, pb: 0.5 }}>
//             <span className="skills-dot" />
//             <span className="skills-dot" />
//             <span className="skills-dot" />
//         </Box>
//     </Box>
// );

// /* ─── Save Success Flash ─────────────────────────────────────────── */
// const SaveSuccessIcon = () => (
//     <span className="skills-check-anim" style={{ display: "inline-flex", alignItems: "center" }}>
//         <CheckCircleIcon sx={{ fontSize: 17, mr: 0.6 }} />
//     </span>
// );
// /* ──────────────────────────────────────────────────────────────── */

// const RecruiterLanding = () => {
//     const navigate = useNavigate();
//     const hasFetchedRef = useRef(false);

//     const [cardData, setCardData] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [searchQuery, setSearchQuery] = useState("");
//     const [showUploadModal, setShowUploadModal] = useState(false);
//     const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

//     const [viewingJdId, setViewingJdId] = useState(null);

//     const [applyJob, setApplyJob] = useState(null);
//     const [isSubmitting, setIsSubmitting] = useState(false);
//     const [editSkillModal, setEditSkillModal] = useState(false);
//     const [selectedJD, setSelectedJD] = useState(null);
//     const [skillData, setSkillData] = useState(null);
//     const [savingSkills, setSavingSkills] = useState(false);

//     // NEW: tracks loading state while fetching JD detail for Skills modal
//     const [skillsLoading, setSkillsLoading] = useState(false);
//     // NEW: tracks which JD id's Skills button was clicked (for button-level spinner)
//     const [openingSkillsId, setOpeningSkillsId] = useState(null);
//     // NEW: save just succeeded (for success flash in button)
//     const [saveSuccess, setSaveSuccess] = useState(false);

//     // ------------------------- API Calls -------------------------
//     const fetchJDs = async () => {
//         setLoading(true);
//         try {
//             const response = await jdApi.getRecruiterJDs();
//             const { status, message, data } = response || {};

//             if (status === 0) {
//                 setSnackbar({ open: true, message: "Network error. Please check your internet connection.", severity: "warning" });
//                 setCardData([]);
//                 return;
//             }
//             if (status >= 500) {
//                 setSnackbar({ open: true, message: message || "Server error while fetching job descriptions.", severity: "error" });
//                 setCardData([]);
//                 return;
//             }
//             if (status === 200) {
//                 setCardData(Array.isArray(data) ? data : []);
//                 return;
//             }
//             setSnackbar({ open: true, message: "Unexpected response from server.", severity: "error" });
//             setCardData([]);
//         } catch (err) {
//             console.error("Error fetching JDs:", err);
//             setSnackbar({ open: true, message: "Failed to fetch job descriptions.", severity: "error" });
//             setCardData([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const viewJd = async (item) => {
//         if (viewingJdId) return;
//         setViewingJdId(item.id);
//         try {
//             const response = await jdApi.viewJD({ jd_id: item.id });
//             const url = response?.data?.download_url;
//             if (url) {
//                 window.open(url, "_blank");
//             } else {
//                 setSnackbar({ open: true, message: "Unable to open JD", severity: "error" });
//             }
//         } catch (error) {
//             console.error("Error viewing JD:", error);
//             setSnackbar({ open: true, message: "Failed to open JD", severity: "error" });
//         } finally {
//             setViewingJdId(null);
//         }
//     };

//     const openSkillEditor = async (item) => {
//         setOpeningSkillsId(item.id);
//         // Open the modal immediately with loading state
//         setSelectedJD(item);
//         setSkillData(null);
//         setEditSkillModal(true);
//         setSkillsLoading(true);
//         setSaveSuccess(false);

//         try {
//             const response = await jdApi.getRecruiterJDDetail(item.id);

//             if (response?.status === 200) {
//                 setSkillData(response.data);
//             } else {
//                 setSnackbar({ open: true, message: "Failed to fetch JD details", severity: "error" });
//                 setEditSkillModal(false);
//             }
//         } catch (error) {
//             console.error(error);
//             setSnackbar({ open: true, message: "Failed to load skills", severity: "error" });
//             setEditSkillModal(false);
//         } finally {
//             setSkillsLoading(false);
//             setOpeningSkillsId(null);
//         }
//     };

//     const handleUploadSuccess = async (data) => {
//         const id = data.data.data?.id;
//         if (!id) return;
//         navigate("/recruiter/analysis", { state: { id } });
//     };

//     const handleApplySuccess = () => {
//         setApplyJob(null);
//         setSnackbar({ open: true, message: "Resume uploaded successfully!", severity: "success" });
//         fetchJDs();
//     };

//     const handleSaveSkills = async () => {
//         try {
//             setSavingSkills(true);
//             setSaveSuccess(false);

//             const payload = {
//                 skill: {
//                     ...skillData.skill,
//                     keySkills: Array.isArray(skillData.skill.keySkills)
//                         ? skillData.skill.keySkills.join(", ")
//                         : skillData.skill.keySkills,
//                     additionalSkills: Array.isArray(skillData.skill.additionalSkills)
//                         ? skillData.skill.additionalSkills.join(", ")
//                         : skillData.skill.additionalSkills,
//                 },
//             };

//             const response = await jdApi.updateRecruiterJDDetail(selectedJD.id, payload);

//             if (response?.status === 200) {
//                 setSaveSuccess(true);
//                 setTimeout(() => {
//                     setSnackbar({ open: true, message: "Skills updated successfully", severity: "success" });
//                     fetchJDs();
//                     setEditSkillModal(false);
//                     setSaveSuccess(false);
//                 }, 700);
//             } else {
//                 setSnackbar({ open: true, message: response?.message || "Failed to update skills", severity: "error" });
//             }
//         } catch (error) {
//             console.error(error);
//             setSnackbar({ open: true, message: "Failed to update skills", severity: "error" });
//         } finally {
//             setSavingSkills(false);
//         }
//     };

//     useEffect(() => {
//         injectStyles();
//         if (!hasFetchedRef.current) {
//             hasFetchedRef.current = true;
//             fetchJDs();
//         }
//     }, []);

//     // ------------------------- Navigation -------------------------
//     const goToCandidates = (jdId) => navigate("/recruiter/llm-matching", { state: { jdId } });

//     const getStatusStyle = (status) => {
//         switch (status?.toLowerCase()) {
//             case "active": return { bg: "#E8F5E9", color: "#2E7D32" };
//             case "draft":  return { bg: "#FFF3E0", color: "#E65100" };
//             case "closed": return { bg: "#FFEBEE", color: "#C62828" };
//             case "open":   return { bg: "#E3F2FD", color: "#1565C0" };
//             default:       return { bg: "#ECEFF1", color: "#37474F" };
//         }
//     };

//     const filteredData = cardData.filter((item) =>
//         item.job_title?.toLowerCase().includes(searchQuery.toLowerCase())
//     );

//     const keySkillsValue = skillData
//         ? (Array.isArray(skillData?.skill?.keySkills)
//             ? skillData.skill.keySkills
//             : typeof skillData?.skill?.keySkills === "string"
//                 ? skillData.skill.keySkills.split(",").map((s) => s.trim()).filter(Boolean)
//                 : [])
//         : [];

//     // ------------------------- Render -------------------------
//     return (
//         <Box sx={{ minHeight: "100vh" }}>
//             <CustomHeader title="Jobs" />

//             {/* Search + Actions */}
//             <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, mb: 2 }}>
//                 <SearchBox
//                     value={searchQuery}
//                     onChange={(e) => setSearchQuery(e.target.value)}
//                     onClear={() => setSearchQuery("")}
//                     placeholder="Search job titles..."
//                     height={38}
//                     fontSize="0.9rem"
//                     minWidth={0}
//                 />
//                 <CustomButton
//                     size="small"
//                     onClick={() => setShowUploadModal(true)}
//                     sx={{ height: 38, px: 2, borderRadius: 1, display: "flex", alignItems: "center", gap: 0.8, fontSize: "0.85rem", whiteSpace: "nowrap", flexShrink: 0 }}
//                 >
//                     <UploadFileIcon sx={{ fontSize: 16 }} />
//                     Upload
//                 </CustomButton>
//                 <CustomButton
//                     size="small"
//                     onClick={() => navigate("/recruiter/jd-generator")}
//                     sx={{ height: 38, px: 2, borderRadius: 1, display: "flex", alignItems: "center", gap: 0.8, fontSize: "0.85rem", whiteSpace: "nowrap", flexShrink: 0 }}
//                 >
//                     <AutoFixHighIcon sx={{ fontSize: 16 }} />
//                     JD Generator
//                 </CustomButton>
//             </Box>

//             {/* JD Cards */}
//             <Box sx={{ px: 3, pb: 3 }}>
//                 {loading ? (
//                     <SkeletonLoader type="card" count={6} />
//                 ) : (
//                     <Fade in={!loading} timeout={400}>
//                         <Box>
//                             {filteredData.length > 0 ? (
//                                 <Grid container spacing={2} alignItems="stretch">
//                                     {filteredData.map((item, index) => {
//                                         const accentColors = ["primary", "success", "warning", "info", "error", "secondary"];
//                                         const currentAccent = accentColors[index % accentColors.length];
//                                         const statusStyle = getStatusStyle(item.status);
//                                         const hasApplicants = item.applications > 0;
//                                         const isViewingThis = viewingJdId === item.id;
//                                         const isOpeningSkills = openingSkillsId === item.id;

//                                         return (
//                                             <Grid key={item.id} item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
//                                                 <CustomCard
//                                                     accentColor={currentAccent}
//                                                     sx={{
//                                                         display: "flex",
//                                                         flexDirection: "column",
//                                                         justifyContent: "space-between",
//                                                         borderRadius: 2,
//                                                         p: 1.5,
//                                                         height: "100%",
//                                                         transition: "box-shadow 0.2s ease",
//                                                         "&:hover": { boxShadow: 4 },
//                                                     }}
//                                                 >
//                                                     {/* Top: Title + Status */}
//                                                     <Box mb={1}>
//                                                         <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5}>
//                                                             <Box sx={{ flex: 1 }}>
//                                                                 <Typography
//                                                                     fontWeight={700}
//                                                                     sx={{
//                                                                         lineHeight: 1.2,
//                                                                         display: "-webkit-box",
//                                                                         WebkitLineClamp: 2,
//                                                                         WebkitBoxOrient: "vertical",
//                                                                         overflow: "hidden",
//                                                                         fontSize: "0.82rem",
//                                                                     }}
//                                                                 >
//                                                                     {item.job_title || "Untitled Role"}
//                                                                 </Typography>
//                                                             </Box>

//                                                             <Stack direction="row" spacing={0.4} alignItems="center">
//                                                                 <Tooltip title={isViewingThis ? "Opening JD…" : "View JD"} arrow>
//                                                                     <span>
//                                                                         <IconButton
//                                                                             size="small"
//                                                                             onClick={() => viewJd(item)}
//                                                                             disabled={!!viewingJdId}
//                                                                             sx={{
//                                                                                 p: 0.4,
//                                                                                 color: isViewingThis ? "primary.main" : "text.secondary",
//                                                                                 "&:hover": { color: "primary.main", bgcolor: "rgba(25,118,210,0.08)" },
//                                                                                 "&.Mui-disabled": { color: isViewingThis ? "primary.main" : "text.disabled" },
//                                                                             }}
//                                                                         >
//                                                                             {isViewingThis
//                                                                                 ? <JdLoader color="#1976d2" />
//                                                                                 : <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
//                                                                         </IconButton>
//                                                                     </span>
//                                                                 </Tooltip>

//                                                                 <Chip
//                                                                     label={item.status || "Open"}
//                                                                     size="small"
//                                                                     sx={{
//                                                                         bgcolor: statusStyle.bg,
//                                                                         color: statusStyle.color,
//                                                                         fontWeight: 600,
//                                                                         fontSize: "0.55rem",
//                                                                         height: 16,
//                                                                         flexShrink: 0,
//                                                                         borderRadius: "8px",
//                                                                         "& .MuiChip-label": { px: 0.8 },
//                                                                     }}
//                                                                 />
//                                                             </Stack>
//                                                         </Stack>
//                                                     </Box>

//                                                     {/* Meta Info */}
//                                                     <Stack spacing={0.4} mb={1.2}>
//                                                         <Stack direction="row" spacing={0.4} alignItems="center">
//                                                             <LocationIcon sx={{ fontSize: 11, color: "text.disabled" }} />
//                                                             <Typography sx={{ fontSize: "0.7rem" }} color="text.secondary" noWrap>
//                                                                 {item.location || "Not Specified"}
//                                                             </Typography>
//                                                         </Stack>
//                                                         <Stack direction="row" spacing={0.5} alignItems="center">
//                                                             <CalendarIcon sx={{ fontSize: 11, color: "text.disabled" }} />
//                                                             <Typography sx={{ fontSize: "0.7rem" }} color="text.secondary">
//                                                                 {item.created_at
//                                                                     ? new Date(item.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
//                                                                     : "Not Specified"}
//                                                             </Typography>
//                                                         </Stack>
//                                                         <Stack direction="row" spacing={0.5} alignItems="center">
//                                                             <BusinessCenterIcon sx={{ fontSize: 11, color: "text.disabled" }} />
//                                                             <Typography sx={{ fontSize: "0.7rem" }} color="text.secondary">
//                                                                 {item.applications || 0} applications
//                                                             </Typography>
//                                                         </Stack>
//                                                     </Stack>

//                                                     {/* Action Buttons */}
//                                                     <Stack direction="row" spacing={0.8}>
//                                                         <CustomButton
//                                                             size="small"
//                                                             fullWidth
//                                                             onClick={() => setApplyJob(item)}
//                                                             sx={{
//                                                                 height: 26,
//                                                                 fontSize: "0.68rem",
//                                                                 borderRadius: 1,
//                                                                 display: "flex",
//                                                                 alignItems: "center",
//                                                                 gap: 0.4,
//                                                                 px: 1,
//                                                                 minWidth: 0,
//                                                             }}
//                                                         >
//                                                             <HowToRegIcon sx={{ fontSize: 12 }} />
//                                                             Apply
//                                                         </CustomButton>

//                                                         <Tooltip
//                                                             title={hasApplicants ? "View candidate matches" : "No candidates yet"}
//                                                             arrow
//                                                             placement="top"
//                                                         >
//                                                             <span style={{ width: "100%" }}>
//                                                                 <CustomButton
//                                                                     size="small"
//                                                                     fullWidth
//                                                                     disabled={!hasApplicants}
//                                                                     onClick={() => goToCandidates(item.id)}
//                                                                     sx={{
//                                                                         height: 26,
//                                                                         fontSize: "0.68rem",
//                                                                         borderRadius: 1,
//                                                                         display: "flex",
//                                                                         alignItems: "center",
//                                                                         gap: 0.4,
//                                                                         px: 1,
//                                                                         minWidth: 0,
//                                                                     }}
//                                                                 >
//                                                                     <PersonSearchIcon sx={{ fontSize: 12 }} />
//                                                                     Matching
//                                                                 </CustomButton>
//                                                             </span>
//                                                         </Tooltip>

//                                                         {/* ── Skills button with inline spinner ── */}
//                                                         <Tooltip title={item.applications > 0 ? "Skills editing disabled when applicants exist" : "Edit interview skills"} arrow placement="top">
//                                                             <span style={{ width: "100%" }}>
//                                                                 <CustomButton
//                                                                     size="small"
//                                                                     fullWidth
//                                                                     disabled={item.applications > 0 || isOpeningSkills}
//                                                                     onClick={() => openSkillEditor(item)}
//                                                                     sx={{
//                                                                         height: 26,
//                                                                         fontSize: "0.68rem",
//                                                                         borderRadius: 1,
//                                                                         px: 1,
//                                                                         minWidth: 0,
//                                                                         display: "flex",
//                                                                         alignItems: "center",
//                                                                         gap: 0.5,
//                                                                         transition: "all 0.2s ease",
//                                                                         ...(isOpeningSkills && {
//                                                                             background: "linear-gradient(135deg,#6366f1,#8b5cf6) !important",
//                                                                             color: "#fff !important",
//                                                                         }),
//                                                                     }}
//                                                                 >
//                                                                     {isOpeningSkills ? (
//                                                                         <>
//                                                                             <CircularProgress size={9} thickness={5} sx={{ color: "#fff" }} />
//                                                                             <span>Loading</span>
//                                                                         </>
//                                                                     ) : (
//                                                                         <>
//                                                                             <WorkspacePremiumIcon sx={{ fontSize: 11 }} />
//                                                                             Skills
//                                                                         </>
//                                                                     )}
//                                                                 </CustomButton>
//                                                             </span>
//                                                         </Tooltip>
//                                                     </Stack>
//                                                 </CustomCard>
//                                             </Grid>
//                                         );
//                                     })}
//                                 </Grid>
//                             ) : (
//                                 <CustomCard
//                                     sx={{
//                                         textAlign: "center",
//                                         borderRadius: 2,
//                                         border: "2px dashed #d0d0d0",
//                                         py: 4,
//                                     }}
//                                 >
//                                     <BusinessCenterIcon sx={{ fontSize: 36, mb: 1, color: "text.disabled" }} />
//                                     <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
//                                         No Job Descriptions Found
//                                     </Typography>
//                                     <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
//                                         {searchQuery ? "Try adjusting your search" : "Upload your first JD to get started"}
//                                     </Typography>
//                                     {!searchQuery && (
//                                         <CustomButton onClick={() => setShowUploadModal(true)}>
//                                             <UploadFileIcon sx={{ fontSize: 16, mr: 1 }} />
//                                             Upload First JD
//                                         </CustomButton>
//                                     )}
//                                 </CustomCard>
//                             )}
//                         </Box>
//                     </Fade>
//                 )}
//             </Box>

//             {/* Apply Modal */}
//             {applyJob && (
//                 <FileUploadModal
//                     open={Boolean(applyJob)}
//                     uploadType="resume"
//                     apiEndpoint={candidateApi.apply}
//                     onClose={() => setApplyJob(null)}
//                     setLoading={setIsSubmitting}
//                     extraPayload={{ job_id: applyJob.id, recruiter_id: applyJob.created_by }}
//                     postUploadHandler={() => handleApplySuccess()}
//                     onError={(error) => {
//                         const errorMessage = error?.planExhaust
//                             ? getPlanExhaustMessage(error.message)
//                             : error?.isRawMessage
//                                 ? error.message
//                                 : ERROR_MESSAGES[error?.message] || ERROR_MESSAGES.UnexpectedError || "Application failed.";
//                         setSnackbar({ open: true, message: errorMessage, severity: error?.severity || "error" });
//                     }}
//                 />
//             )}

//             {/* JD Upload Modal */}
//             {showUploadModal && (
//                 <FileUploadModal
//                     open={showUploadModal}
//                     uploadType="jd"
//                     apiEndpoint={jdApi.createRecruiterJD}
//                     onClose={() => setShowUploadModal(false)}
//                     setLoading={setLoading}
//                     onSuccess={(data) => {
//                         handleUploadSuccess(data);
//                         setSnackbar({ open: true, message: "JD uploaded successfully!", severity: "success" });
//                     }}
//                     onError={(error) => {
//                         const errorMessage = error?.planExhaust
//                             ? getPlanExhaustMessage(error.message)
//                             : error?.isRawMessage
//                                 ? error.message
//                                 : ERROR_MESSAGES[error?.message] || ERROR_MESSAGES.UnexpectedError || "JD upload failed.";
//                         setSnackbar({ open: true, message: errorMessage, severity: error?.severity || "error" });
//                     }}
//                 />
//             )}

//             {/* ══════════════════════════════════════════════════
//                 BEAUTIFUL SKILLS MODAL
//             ══════════════════════════════════════════════════ */}
//             {editSkillModal && (
//                 <Dialog
//                     open={editSkillModal}
//                     onClose={() => { if (!savingSkills) setEditSkillModal(false); }}
//                     maxWidth="sm"
//                     fullWidth
//                     PaperProps={{
//                         sx: {
//                             borderRadius: "16px",
//                             overflow: "hidden",
//                             boxShadow: "0 24px 64px rgba(99,102,241,0.18), 0 8px 32px rgba(0,0,0,0.12)",
//                             fontFamily: "'DM Sans', sans-serif",
//                         },
//                     }}
//                 >
//                     {/* ── Gradient header band ── */}
//                     <Box
//                         className="skills-header-band"
//                         sx={{ px: 3, pt: 2.5, pb: 2, position: "relative" }}
//                     >
//                         {/* close button */}
//                         <IconButton
//                             size="small"
//                             onClick={() => { if (!savingSkills) setEditSkillModal(false); }}
//                             disabled={savingSkills}
//                             sx={{
//                                 position: "absolute",
//                                 top: 10,
//                                 right: 10,
//                                 color: "rgba(255,255,255,0.75)",
//                                 "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.12)" },
//                             }}
//                         >
//                             <CloseIcon sx={{ fontSize: 16 }} />
//                         </IconButton>

//                         <Stack direction="row" alignItems="center" spacing={1.2}>
//                             <Box
//                                 sx={{
//                                     width: 36,
//                                     height: 36,
//                                     borderRadius: "10px",
//                                     bgcolor: "rgba(255,255,255,0.18)",
//                                     display: "flex",
//                                     alignItems: "center",
//                                     justifyContent: "center",
//                                     backdropFilter: "blur(4px)",
//                                     flexShrink: 0,
//                                 }}
//                             >
//                                 <PsychologyAltIcon sx={{ fontSize: 20, color: "#fff" }} />
//                             </Box>
//                             <Box>
//                                 <Typography
//                                     sx={{
//                                         fontSize: "1rem",
//                                         fontWeight: 700,
//                                         color: "#fff",
//                                         fontFamily: "'DM Sans', sans-serif",
//                                         lineHeight: 1.2,
//                                     }}
//                                 >
//                                     Interview Skills
//                                 </Typography>
//                                 <Typography
//                                     sx={{
//                                         fontSize: "0.72rem",
//                                         color: "rgba(255,255,255,0.72)",
//                                         fontFamily: "'DM Sans', sans-serif",
//                                         mt: 0.2,
//                                     }}
//                                 >
//                                     {selectedJD?.job_title || "Job Role"}
//                                 </Typography>
//                             </Box>
//                         </Stack>

//                         {/* Subtle decorative circle */}
//                         <Box sx={{
//                             position: "absolute", right: -18, top: -18,
//                             width: 90, height: 90, borderRadius: "50%",
//                             bgcolor: "rgba(255,255,255,0.07)",
//                             pointerEvents: "none",
//                         }} />
//                         <Box sx={{
//                             position: "absolute", right: 30, bottom: -28,
//                             width: 60, height: 60, borderRadius: "50%",
//                             bgcolor: "rgba(255,255,255,0.05)",
//                             pointerEvents: "none",
//                         }} />
//                     </Box>

//                     {/* ── Content ── */}
//                     <DialogContent sx={{ px: 3, pt: 2.5, pb: 1.5, bgcolor: "#fafbff" }}>
//                         {skillsLoading ? (
//                             <SkillsLoadingState />
//                         ) : skillData ? (
//                             <Fade in timeout={350}>
//                                 <Box>
//                                     {/* Helper hint */}
//                                     <Box
//                                         sx={{
//                                             display: "flex",
//                                             alignItems: "center",
//                                             gap: 0.8,
//                                             bgcolor: "#eef2ff",
//                                             border: "1px solid #c7d2fe",
//                                             borderRadius: "10px",
//                                             px: 1.5,
//                                             py: 1,
//                                             mb: 2,
//                                         }}
//                                     >
//                                         <AddIcon sx={{ fontSize: 13, color: "#6366f1", flexShrink: 0 }} />
//                                         <Typography sx={{ fontSize: "0.7rem", color: "#4338ca", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.4 }}>
//                                             Type a skill and press <strong>Enter</strong> to add it. Click the × on a chip to remove it.
//                                         </Typography>
//                                     </Box>

//                                     {/* Key Skills count badge */}
//                                     <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
//                                         <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", fontFamily: "'DM Sans', sans-serif" }}>
//                                             Key Skills
//                                         </Typography>
//                                         <Chip
//                                             label={`${keySkillsValue.length} skill${keySkillsValue.length !== 1 ? "s" : ""}`}
//                                             size="small"
//                                             sx={{
//                                                 height: 20,
//                                                 fontSize: "0.6rem",
//                                                 fontWeight: 600,
//                                                 bgcolor: keySkillsValue.length > 0 ? "#e0e7ff" : "#f3f4f6",
//                                                 color: keySkillsValue.length > 0 ? "#4338ca" : "#9ca3af",
//                                                 border: "none",
//                                                 "& .MuiChip-label": { px: 1 },
//                                             }}
//                                         />
//                                     </Stack>

//                                     <Box className="skills-dialog-chip">
//                                         <Autocomplete
//                                             multiple
//                                             freeSolo
//                                             options={[]}
//                                             value={keySkillsValue}
//                                             onChange={(e, value) =>
//                                                 setSkillData((prev) => ({
//                                                     ...prev,
//                                                     skill: { ...prev.skill, keySkills: value },
//                                                 }))
//                                             }
//                                             renderInput={(params) => (
//                                                 <TextField
//                                                     {...params}
//                                                     placeholder={keySkillsValue.length === 0 ? "e.g. React, Node.js, SQL…" : "Add more…"}
//                                                     variant="outlined"
//                                                     sx={{
//                                                         "& .MuiOutlinedInput-root": {
//                                                             borderRadius: "12px",
//                                                             bgcolor: "#fff",
//                                                             fontSize: "0.82rem",
//                                                             fontFamily: "'DM Sans', sans-serif",
//                                                             "& fieldset": { borderColor: "#e0e7ff", borderWidth: "1.5px" },
//                                                             "&:hover fieldset": { borderColor: "#a5b4fc" },
//                                                             "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "2px" },
//                                                         },
//                                                         "& .MuiInputBase-input::placeholder": {
//                                                             color: "#9ca3af",
//                                                             fontSize: "0.78rem",
//                                                         },
//                                                     }}
//                                                 />
//                                             )}
//                                             renderTags={(value, getTagProps) =>
//                                                 value.map((option, index) => (
//                                                     <Chip
//                                                         key={index}
//                                                         label={option}
//                                                         size="small"
//                                                         className="skills-chip-anim"
//                                                         {...getTagProps({ index })}
//                                                     />
//                                                 ))
//                                             }
//                                         />
//                                     </Box>
//                                 </Box>
//                             </Fade>
//                         ) : null}
//                     </DialogContent>

//                     {/* ── Actions ── */}
//                     <DialogActions
//                         sx={{
//                             px: 3,
//                             py: 2,
//                             bgcolor: "#fafbff",
//                             borderTop: "1px solid #eef2ff",
//                             gap: 1,
//                         }}
//                     >
//                         <Button
//                             onClick={() => { setEditSkillModal(false); fetchJDs(); }}
//                             disabled={savingSkills}
//                             sx={{
//                                 borderRadius: "10px",
//                                 fontSize: "0.78rem",
//                                 fontFamily: "'DM Sans', sans-serif",
//                                 fontWeight: 500,
//                                 color: "#6b7280",
//                                 textTransform: "none",
//                                 px: 2.5,
//                                 "&:hover": { bgcolor: "#f3f4f6", color: "#374151" },
//                             }}
//                         >
//                             Cancel
//                         </Button>

//                         <Button
//                             variant="contained"
//                             onClick={handleSaveSkills}
//                             disabled={savingSkills || skillsLoading || !skillData}
//                             startIcon={
//                                 savingSkills ? null
//                                     : saveSuccess ? <SaveSuccessIcon /> : null
//                             }
//                             sx={{
//                                 borderRadius: "10px",
//                                 fontSize: "0.78rem",
//                                 fontFamily: "'DM Sans', sans-serif",
//                                 fontWeight: 600,
//                                 textTransform: "none",
//                                 px: 2.5,
//                                 minWidth: 110,
//                                 height: 36,
//                                 background: saveSuccess
//                                     ? "linear-gradient(135deg, #059669, #10b981)"
//                                     : "linear-gradient(135deg, #6366f1, #8b5cf6)",
//                                 boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
//                                 transition: "background 0.4s ease, box-shadow 0.2s ease",
//                                 "&:hover": {
//                                     background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
//                                     boxShadow: "0 6px 18px rgba(99,102,241,0.45)",
//                                 },
//                                 "&.Mui-disabled": {
//                                     background: "#e5e7eb",
//                                     boxShadow: "none",
//                                     color: "#9ca3af",
//                                 },
//                                 display: "flex",
//                                 alignItems: "center",
//                                 gap: 0.7,
//                             }}
//                         >
//                             {savingSkills ? (
//                                 <>
//                                     <span className="skills-save-spinner" />
//                                     Saving…
//                                 </>
//                             ) : saveSuccess ? (
//                                 <>
//                                     <CheckCircleIcon sx={{ fontSize: 15 }} />
//                                     Saved!
//                                 </>
//                             ) : (
//                                 "Save Skills"
//                             )}
//                         </Button>
//                     </DialogActions>
//                 </Dialog>
//             )}

//             {/* Snackbar */}
//             {snackbar.open && (
//                 <CustomSnackbar
//                     open={snackbar.open}
//                     message={snackbar.message}
//                     severity={snackbar.severity}
//                     onClose={() => setSnackbar({ ...snackbar, open: false })}
//                 />
//             )}
//         </Box>
//     );
// };

// export default RecruiterLanding;


// import React, { useState, useEffect, useRef } from "react";
// import {
//     Box,
//     Typography,
//     Fade,
//     Tooltip,
//     Stack,
//     Chip,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     TextField,
//     Button,
//     Autocomplete,
//     CircularProgress,
//     Snackbar,
//     Alert,
//     InputAdornment
// } from "@mui/material";
// import {
//     LocationOn as LocationIcon,
//     CalendarToday as CalendarIcon,
//     UploadFile as UploadFileIcon,
//     PersonSearch as PersonSearchIcon,
//     HowToReg as HowToRegIcon,
//     PsychologyAlt as PsychologyAltIcon,
//     CheckCircle as CheckCircleIcon,
//     Close as CloseIcon,
//     WorkspacePremium as WorkspacePremiumIcon,
//     Add as AddIcon,
//     Search as SearchIcon,
//     AutoAwesome as AutoAwesomeIcon,
//     PeopleAlt as PeopleAltIcon,
//     ArrowOutward as ArrowOutwardIcon,
//     BusinessCenter as BusinessCenterIcon,
// } from "@mui/icons-material";
// import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
// import IconButton from "@mui/material/IconButton";

// // Actual routing and API imports
// import { useNavigate } from "react-router-dom";
// import { jdApi } from "../../api/jobdescriptionApi";
// import { candidateApi } from "../../api/candidateApi";
// import { ERROR_MESSAGES, getPlanExhaustMessage } from "../../components/common/StatusDictionary";
// import FileUploadModal from "../FileUploadModal";

// /* ------------------------------------------------------------------
//    GLOBAL STYLES & ANIMATIONS
// ------------------------------------------------------------------ */
// const modernStyles = `
// @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

// body {
//     background-color: #F8FAFC;
//     font-family: 'Plus Jakarta Sans', sans-serif;
//     margin: 0;
//     -webkit-font-smoothing: antialiased;
// }

// /* Smooth Skeleton Shimmer */
// @keyframes shimmer {
//     0% { background-position: -1000px 0; }
//     100% { background-position: 1000px 0; }
// }
// .skeleton-box {
//     background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
//     background-size: 1000px 100%;
//     animation: shimmer 2s infinite linear;
//     border-radius: 14px;
// }

// /* Spinner for buttons */
// @keyframes spin { 100% { transform: rotate(360deg); } }
// .loader-ring {
//     width: 12px; height: 12px;
//     border: 2px solid rgba(255,255,255,0.3);
//     border-top-color: #fff;
//     border-radius: 50%;
//     animation: spin 0.8s linear infinite;
// }
// .loader-ring-blue {
//     border-color: rgba(99,102,241,0.2);
//     border-top-color: #6366f1;
// }

// /* Custom Scrollbar */
// ::-webkit-scrollbar { width: 6px; }
// ::-webkit-scrollbar-track { background: transparent; }
// ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
// ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
// `;

// let styleInjected = false;
// const injectStyles = () => {
//     if (!styleInjected) {
//         const tag = document.createElement("style");
//         tag.textContent = modernStyles;
//         document.head.appendChild(tag);
//         styleInjected = true;
//     }
// };

// /* ------------------------------------------------------------------
//    REUSABLE COMPONENTS
// ------------------------------------------------------------------ */
// const SkeltonGrid = () => (
//     <Box sx={{
//         display: 'grid',
//         gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 1fr))' },
//         gap: 2.5
//     }}>
//         {[1, 2, 3, 4, 5, 6].map((i) => (
//             <Box
//                 key={i}
//                 className="skeleton-box"
//                 sx={{
//                     height: 200,
//                     borderRadius: '20px',
//                     border: '1px solid #f1f5f9'
//                 }}
//             />
//         ))}
//     </Box>
// );

// /* ------------------------------------------------------------------
//    MAIN COMPONENT
// ------------------------------------------------------------------ */
// const RecruiterLanding = () => {
//     const navigate = useNavigate();
//     const hasFetchedRef = useRef(false);

//     // State
//     const [cardData, setCardData] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [searchQuery, setSearchQuery] = useState("");

//     // Modals
//     const [showUploadModal, setShowUploadModal] = useState(false);
//     const [applyJob, setApplyJob] = useState(null);
//     const [editSkillModal, setEditSkillModal] = useState(false);

//     // Trackers
//     const [viewingJdId, setViewingJdId] = useState(null);
//     const [isSubmitting, setIsSubmitting] = useState(false);
//     const [selectedJD, setSelectedJD] = useState(null);
//     const [skillData, setSkillData] = useState(null);
//     const [skillsLoading, setSkillsLoading] = useState(false);
//     const [savingSkills, setSavingSkills] = useState(false);
//     const [openingSkillsId, setOpeningSkillsId] = useState(null);
//     const [saveSuccess, setSaveSuccess] = useState(false);

//     const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

//     // Initial Fetch
//     useEffect(() => {
//         injectStyles();
//         if (!hasFetchedRef.current) {
//             hasFetchedRef.current = true;
//             fetchJDs();
//         }
//     }, []);

//     const fetchJDs = async () => {
//         setLoading(true);
//         try {
//             const response = await jdApi.getRecruiterJDs();
//             if (response?.status === 200) {
//                 setCardData(Array.isArray(response.data) ? response.data : []);
//             }
//         } catch (err) {
//             setSnackbar({ open: true, message: "Failed to fetch job descriptions.", severity: "error" });
//         } finally {
//             setLoading(false);
//         }
//     };

//     const viewJd = async (item) => {
//         if (viewingJdId) return;
//         setViewingJdId(item.id);
//         try {
//             const response = await jdApi.viewJD({ jd_id: item.id });
//             const url = response?.data?.download_url;
//             if (url) window.open(url, "_blank");
//         } catch (error) {
//             setSnackbar({ open: true, message: "Failed to open JD", severity: "error" });
//         } finally {
//             setViewingJdId(null);
//         }
//     };

//     const openSkillEditor = async (item) => {
//         setOpeningSkillsId(item.id);
//         setSelectedJD(item);
//         setSkillData(null);
//         setEditSkillModal(true);
//         setSkillsLoading(true);
//         setSaveSuccess(false);

//         try {
//             const response = await jdApi.getRecruiterJDDetail(item.id);
//             if (response?.status === 200) setSkillData(response.data);
//             else {
//                 setSnackbar({ open: true, message: "Failed to fetch JD details", severity: "error" });
//                 setEditSkillModal(false);
//             }
//         } catch (error) {
//             setSnackbar({ open: true, message: "Failed to load skills", severity: "error" });
//             setEditSkillModal(false);
//         } finally {
//             setSkillsLoading(false);
//             setOpeningSkillsId(null);
//         }
//     };

//     const handleSaveSkills = async () => {
//         try {
//             setSavingSkills(true);
//             setSaveSuccess(false);

//             // Format array to string if necessary based on your original logic
//             const payload = {
//                 skill: {
//                     ...skillData.skill,
//                     keySkills: Array.isArray(skillData.skill.keySkills)
//                         ? skillData.skill.keySkills.join(", ")
//                         : skillData.skill.keySkills,
//                     additionalSkills: Array.isArray(skillData.skill.additionalSkills)
//                         ? skillData.skill.additionalSkills.join(", ")
//                         : skillData.skill.additionalSkills,
//                 },
//             };

//             const response = await jdApi.updateRecruiterJDDetail(selectedJD.id, payload);

//             if (response?.status === 200) {
//                 setSaveSuccess(true);
//                 setTimeout(() => {
//                     setSnackbar({ open: true, message: "Skills updated successfully", severity: "success" });
//                     fetchJDs();
//                     setEditSkillModal(false);
//                     setSaveSuccess(false);
//                 }, 800);
//             } else {
//                 setSnackbar({ open: true, message: response?.message || "Failed to update skills", severity: "error" });
//             }
//         } catch (error) {
//             setSnackbar({ open: true, message: "Failed to update skills", severity: "error" });
//         } finally {
//             setSavingSkills(false);
//         }
//     };

//     const handleUploadSuccess = async (data) => {
//         const id = data?.data?.data?.id;
//         if (id) {
//             navigate("/recruiter/analysis", { state: { id } });
//         } else {
//             // Fallback behavior if just refreshing data
//             setSnackbar({ open: true, message: "JD uploaded successfully!", severity: "success" });
//             fetchJDs();
//         }
//     };

//     const handleApplySuccess = () => {
//         setApplyJob(null);
//         setSnackbar({ open: true, message: "Resume uploaded successfully!", severity: "success" });
//         fetchJDs();
//     };

//     const getStatusStyle = (status) => {
//         switch (status?.toLowerCase()) {
//             case "active": return { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" };
//             case "draft": return { bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
//             case "closed": return { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
//             case "open": return { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" };
//             default: return { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" };
//         }
//     };

//     const filteredData = cardData.filter((item) =>
//         item.job_title?.toLowerCase().includes(searchQuery.toLowerCase())
//     );

//     const keySkillsValue = skillData
//         ? (Array.isArray(skillData?.skill?.keySkills)
//             ? skillData.skill.keySkills
//             : typeof skillData?.skill?.keySkills === "string"
//                 ? skillData.skill.keySkills.split(",").map((s) => s.trim()).filter(Boolean)
//                 : [])
//         : [];

//     return (
//         <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", p: { xs: 1.5, md: 3, lg: 4 }, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

//             {/* 1. Header & Controls Section */}
//             <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 4 }}>

//                 {/* Title Area */}
//                 <Box>
//                     <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 1.5 }}>
//                         Workspace
//                         <Chip label={`${cardData.length} Openings`} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 700, borderRadius: '6px', fontSize: '0.65rem', height: 22 }} />
//                     </Typography>
//                     <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.5, fontWeight: 500 }}>
//                         Manage your job postings and track applicants seamlessly.
//                     </Typography>
//                 </Box>

//                 {/* Control Actions */}
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

//                     {/* Modern Glassy Search Bar */}
//                     <TextField
//                         value={searchQuery}
//                         onChange={(e) => setSearchQuery(e.target.value)}
//                         placeholder="Search positions..."
//                         InputProps={{
//                             startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
//                         }}
//                         sx={{
//                             minWidth: { xs: '100%', sm: 240 },
//                             '& .MuiOutlinedInput-root': {
//                                 height: 38,
//                                 bgcolor: '#ffffff',
//                                 borderRadius: '10px',
//                                 fontSize: '0.8rem',
//                                 fontWeight: 500,
//                                 fontFamily: "'Plus Jakarta Sans', sans-serif",
//                                 transition: 'all 0.2s ease',
//                                 boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
//                                 '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1px' },
//                                 '&:hover fieldset': { borderColor: '#cbd5e1' },
//                                 '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: '2px' },
//                                 '&.Mui-focused': { boxShadow: '0 4px 12px rgba(99,102,241,0.1)' }
//                             }
//                         }}
//                     />

//                     <Button
//                         onClick={() => setShowUploadModal(true)}
//                         startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
//                         sx={{
//                             height: 38, px: 2.5, borderRadius: '10px', flex: { xs: 1, sm: 'none' },
//                             textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', fontFamily: 'inherit',
//                             bgcolor: '#ffffff', color: '#334155', border: '1px solid #e2e8f0',
//                             boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
//                             transition: 'all 0.2s',
//                             '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1', transform: 'translateY(-1px)', boxShadow: '0 4px 8px rgba(0,0,0,0.05)' }
//                         }}
//                     >
//                         Upload
//                     </Button>

//                     <Button
//                         onClick={() => navigate("/recruiter/jd-generator")}
//                         startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
//                         sx={{
//                             height: 38, px: 2.5, borderRadius: '10px', flex: { xs: 1, sm: 'none' },
//                             textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', fontFamily: 'inherit',
//                             background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)', color: '#fff',
//                             boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
//                             transition: 'all 0.2s',
//                             '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 16px rgba(59,130,246,0.4)' }
//                         }}
//                     >
//                         Generate
//                     </Button>
//                 </Box>
//             </Box>

//             {/* 2. Grid Area */}
//             <Box>
//                 {loading ? (
//                     <SkeltonGrid />
//                 ) : (
//                     <Fade in={!loading} timeout={500}>
//                         <Box>
//                             {filteredData.length > 0 ? (
//                                 <Box sx={{
//                                     display: 'grid',
//                                     gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 1fr))' },
//                                     gap: 2.5
//                                 }}>
//                                     {filteredData.map((item) => {
//                                         const statusStyle = getStatusStyle(item.status);
//                                         const hasApplicants = item.applications > 0;
//                                         const isViewingThis = viewingJdId === item.id;
//                                         const isOpeningSkills = openingSkillsId === item.id;

//                                         return (
//                                             /* THE CARD */
//                                             <Box
//                                                 key={item.id}
//                                                 sx={{
//                                                     display: 'flex',
//                                                     flexDirection: 'column',
//                                                     bgcolor: '#ffffff',
//                                                     borderRadius: '20px',
//                                                     p: 2.5,
//                                                     border: '1px solid rgba(226, 232, 240, 0.8)',
//                                                     boxShadow: '0 8px 24px -10px rgba(15, 23, 42, 0.05)',
//                                                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
//                                                     position: 'relative',
//                                                     overflow: 'hidden',
//                                                     '&:hover': {
//                                                         boxShadow: '0 16px 32px -12px rgba(15, 23, 42, 0.1)',
//                                                         transform: 'translateY(-3px)',
//                                                         borderColor: '#cbd5e1'
//                                                     },
//                                                 }}
//                                             >
//                                                 {/* Decorative background blur inside card */}
//                                                 <Box sx={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

//                                                 {/* Content Wrapper (grows to push footer down) */}
//                                                 <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

//                                                     {/* Card Header */}
//                                                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1.2 }}>

//                                                         {/* Icon + Title */}
//                                                         <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start', overflow: 'hidden' }}>
//                                                             <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
//                                                                 <BusinessCenterIcon sx={{ fontSize: 16, color: '#64748b' }} />
//                                                             </Box>
//                                                             <Box sx={{ overflow: 'hidden', flex: 1 }}>
//                                                                 {/* TRUNCATED TITLE WITH TOOLTIP */}
//                                                                 <Tooltip
//                                                                     title={item.job_title}
//                                                                     placement="top"
//                                                                     arrow
//                                                                     componentsProps={{ tooltip: { sx: { bgcolor: '#0f172a', fontWeight: 500, fontSize: '0.75rem', fontFamily: 'inherit', p: 1.2, borderRadius: '6px' } } }}
//                                                                 >
//                                                                     <Typography
//                                                                         sx={{
//                                                                             fontSize: "0.95rem",
//                                                                             fontWeight: 800,
//                                                                             color: "#0f172a",
//                                                                             lineHeight: 1.3,
//                                                                             whiteSpace: "nowrap",
//                                                                             overflow: "hidden",
//                                                                             textOverflow: "ellipsis",
//                                                                             cursor: 'default',
//                                                                         }}
//                                                                     >
//                                                                         {item.job_title}
//                                                                     </Typography>
//                                                                 </Tooltip>
//                                                                 {/* <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>
//                                                                     ID: #{item.id}
//                                                                 </Typography> */}
//                                                             </Box>
//                                                         </Box>

//                                                         {/* Action (View) & Status */}
//                                                         <Stack direction="row" alignItems="center" spacing={0.8} sx={{ flexShrink: 0 }}>
//                                                             <IconButton
//                                                                 size="small"
//                                                                 onClick={() => viewJd(item)}
//                                                                 disabled={!!viewingJdId}
//                                                                 sx={{ p: 0.4, border: '1px solid #e2e8f0', bgcolor: '#fff', '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' } }}
//                                                             >
//                                                                 {isViewingThis ? <span className="loader-ring loader-ring-blue" /> : <ArrowOutwardIcon sx={{ fontSize: 14, color: '#475569' }} />}
//                                                             </IconButton>
//                                                             <Chip
//                                                                 label={item.status}
//                                                                 size="small"
//                                                                 sx={{
//                                                                     bgcolor: statusStyle.bg, color: statusStyle.color,
//                                                                     border: `1px solid ${statusStyle.border}`,
//                                                                     fontWeight: 700, fontSize: "0.6rem", height: 22, borderRadius: "6px", px: 0.5
//                                                                 }}
//                                                             />
//                                                         </Stack>
//                                                     </Box>

//                                                     {/* Meta Details (List) */}
//                                                     <Stack spacing={1} mb={3}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                                             <LocationIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
//                                                             <Typography sx={{ fontSize: "0.75rem", color: "#475569", fontWeight: 500 }} noWrap>
//                                                                 {item.location || "Not Specified"}
//                                                             </Typography>
//                                                         </Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                                             <CalendarIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
//                                                             <Typography sx={{ fontSize: "0.75rem", color: "#475569", fontWeight: 500 }}>
//                                                                 Created {item.created_at ? new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A"}
//                                                             </Typography>
//                                                         </Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                                             <PeopleAltIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
//                                                             <Typography sx={{ fontSize: "0.75rem", color: "#475569", fontWeight: 600 }}>
//                                                                 {item.applications || 0} Applicants pending
//                                                             </Typography>
//                                                         </Box>
//                                                     </Stack>
//                                                 </Box>

//                                                 {/* Card Footer (Buttons forced to bottom by flexGrow above) */}
//                                                 <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>

//                                                     {/* Apply Button (Indigo Tint) */}
//                                                     <Button
//                                                         onClick={() => setApplyJob(item)}
//                                                         sx={{
//                                                             flex: 1, height: 32, borderRadius: '10px',
//                                                             bgcolor: '#eef2ff', color: '#4f46e5', fontWeight: 700, fontSize: '0.75rem', textTransform: 'none', fontFamily: 'inherit',
//                                                             '&:hover': { bgcolor: '#4f46e5', color: '#fff' }
//                                                         }}
//                                                     >
//                                                         Apply
//                                                     </Button>

//                                                     {/* Match Button (Emerald Tint) */}
//                                                     <Button
//                                                         disabled={!hasApplicants}
//                                                         onClick={() => navigate("/recruiter/llm-matching", { state: { jdId: item.id } })}
//                                                         sx={{
//                                                             flex: 1, height: 32, borderRadius: '10px',
//                                                             bgcolor: '#ecfdf5', color: '#059669', fontWeight: 700, fontSize: '0.75rem', textTransform: 'none', fontFamily: 'inherit',
//                                                             '&:hover': { bgcolor: '#059669', color: '#fff' },
//                                                             '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#94a3b8' }
//                                                         }}
//                                                     >
//                                                         Match
//                                                     </Button>

//                                                     {/* Skills Button (Fuchsia Tint) */}
//                                                     <Button
//                                                         disabled={item.applications > 0 || isOpeningSkills}
//                                                         onClick={() => openSkillEditor(item)}
//                                                         sx={{
//                                                             flex: 1, height: 32, borderRadius: '10px',
//                                                             bgcolor: '#fdf4ff', color: '#c026d3', fontWeight: 700, fontSize: '0.75rem', textTransform: 'none', fontFamily: 'inherit',
//                                                             '&:hover': { bgcolor: '#c026d3', color: '#fff' },
//                                                             '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#94a3b8' }
//                                                         }}
//                                                     >
//                                                         {isOpeningSkills ? <span className="loader-ring loader-ring-blue" /> : "Skills"}
//                                                     </Button>
//                                                 </Box>
//                                             </Box>
//                                         );
//                                     })}
//                                 </Box>
//                             ) : (
//                                 /* Empty State */
//                                 <Box sx={{ maxWidth: 450, mx: 'auto', textAlign: "center", borderRadius: '20px', border: "2px dashed #cbd5e1", py: 6, bgcolor: "transparent" }}>
//                                     <Box sx={{ width: 64, height: 64, borderRadius: '16px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
//                                         <BusinessCenterIcon sx={{ fontSize: 28, color: "#94a3b8" }} />
//                                     </Box>
//                                     <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1, fontWeight: 800, color: "#1e293b", fontFamily: 'inherit' }}>
//                                         No Openings Found
//                                     </Typography>
//                                     <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.8rem', mb: 3.5, fontWeight: 500, fontFamily: 'inherit' }}>
//                                         {searchQuery ? "Try adjusting your search criteria." : "Get started by uploading your first Job Description."}
//                                     </Typography>
//                                     {!searchQuery && (
//                                         <Button
//                                             variant="contained"
//                                             onClick={() => setShowUploadModal(true)}
//                                             startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
//                                             sx={{ borderRadius: '10px', fontSize: '0.8rem', textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', bgcolor: '#0f172a', color: '#fff', '&:hover': { bgcolor: '#334155' }, px: 2.5, py: 1 }}
//                                         >
//                                             Upload First JD
//                                         </Button>
//                                     )}
//                                 </Box>
//                             )}
//                         </Box>
//                     </Fade>
//                 )}
//             </Box>

//             {/* 3. Modals & Notifications */}

//             {/* Real Upload Modals Hooked up to candidateApi and jdApi */}
//             {applyJob && (
//                 <FileUploadModal
//                     open={Boolean(applyJob)}
//                     uploadType="resume"
//                     apiEndpoint={candidateApi.apply}
//                     onClose={() => setApplyJob(null)}
//                     setLoading={setIsSubmitting}
//                     extraPayload={{ job_id: applyJob.id, recruiter_id: applyJob.created_by }}
//                     postUploadHandler={() => handleApplySuccess()}
//                     onError={(error) => {
//                         const errorMessage = error?.planExhaust
//                             ? getPlanExhaustMessage(error.message)
//                             : error?.isRawMessage
//                                 ? error.message
//                                 : ERROR_MESSAGES[error?.message] || ERROR_MESSAGES.UnexpectedError || "Application failed.";
//                         setSnackbar({ open: true, message: errorMessage, severity: error?.severity || "error" });
//                     }}
//                 />
//             )}

//             {showUploadModal && (
//                 <FileUploadModal
//                     open={showUploadModal}
//                     uploadType="jd"
//                     apiEndpoint={jdApi.createRecruiterJD}
//                     onClose={() => setShowUploadModal(false)}
//                     setLoading={setLoading}
//                     onSuccess={(data) => {
//                         handleUploadSuccess(data);
//                     }}
//                     onError={(error) => {
//                         const errorMessage = error?.planExhaust
//                             ? getPlanExhaustMessage(error.message)
//                             : error?.isRawMessage
//                                 ? error.message
//                                 : ERROR_MESSAGES[error?.message] || ERROR_MESSAGES.UnexpectedError || "JD upload failed.";
//                         setSnackbar({ open: true, message: errorMessage, severity: error?.severity || "error" });
//                     }}
//                 />
//             )}

//             {/* High-End Skills Modal */}
//             {editSkillModal && (
//                 <Dialog open={editSkillModal} onClose={() => { if (!savingSkills) setEditSkillModal(false); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.2)' } }}>

//                     {/* Header with Mesh Gradient */}
//                     <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", px: 3, pt: 3, pb: 2, position: "relative", overflow: 'hidden' }}>
//                         {/* Abstract background shapes */}
//                         <Box sx={{ position: 'absolute', top: -25, right: -15, width: 85, height: 85, background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />

//                         <IconButton size="small" onClick={() => { if (!savingSkills) setEditSkillModal(false); }} disabled={savingSkills} sx={{ position: "absolute", top: 12, right: 12, color: "#94a3b8", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}>
//                             <CloseIcon sx={{ fontSize: 18 }} />
//                         </IconButton>

//                         <Stack direction="row" alignItems="center" spacing={2}>
//                             <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.1)", border: '1px solid rgba(255,255,255,0.1)', display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: 'blur(10px)' }}>
//                                 <PsychologyAltIcon sx={{ fontSize: 24, color: "#818cf8" }} />
//                             </Box>
//                             <Box>
//                                 <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#fff", lineHeight: 1.2, fontFamily: 'inherit' }}>Required Skills Setup</Typography>
//                                 <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", mt: 0.5, fontWeight: 500, fontFamily: 'inherit' }}>{selectedJD?.job_title || "Job Role"}</Typography>
//                             </Box>
//                         </Stack>
//                     </Box>

//                     {/* Content */}
//                     <DialogContent sx={{ px: 3, pt: 3, pb: 2.5, bgcolor: '#fff' }}>
//                         {skillsLoading ? (
//                             <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: '#6366f1' }} /></Box>
//                         ) : skillData ? (
//                             <Fade in timeout={350}>
//                                 <Box>
//                                     <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, bgcolor: "#f8fafc", border: '1px solid #e2e8f0', borderRadius: '12px', px: 2, py: 1.5, mb: 3 }}>
//                                         <AutoAwesomeIcon sx={{ fontSize: 16, color: "#6366f1", mt: 0.1 }} />
//                                         <Typography sx={{ fontSize: "0.75rem", color: "#475569", fontWeight: 500, fontFamily: 'inherit', lineHeight: 1.5 }}>
//                                             Type a core competency and press <strong>Enter</strong>. These skills will be used by the LLM engine during candidate evaluation.
//                                         </Typography>
//                                     </Box>

//                                     <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#1e293b", mb: 1.5, fontFamily: 'inherit' }}>Mandatory Evaluation Skills</Typography>
//                                     <Autocomplete
//                                         multiple
//                                         freeSolo
//                                         options={[]}
//                                         value={keySkillsValue}
//                                         onChange={(e, value) => setSkillData({ ...skillData, skill: { ...skillData.skill, keySkills: value } })}
//                                         renderInput={(params) => (
//                                             <TextField {...params} placeholder="e.g. React, Node.js, SQL…" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc', fontSize: '0.85rem', '& fieldset': { borderColor: '#cbd5e1' }, '&:hover fieldset': { borderColor: '#94a3b8' }, '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: '2px' } } }} />
//                                         )}
//                                         renderTags={(value, getTagProps) =>
//                                             value.map((option, index) => (
//                                                 <Chip key={index} label={option} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 700, fontSize: '0.7rem', fontFamily: 'inherit', borderRadius: '6px', m: 0.4, border: '1px solid #c7d2fe' }} {...getTagProps({ index })} />
//                                             ))
//                                         }
//                                     />
//                                 </Box>
//                             </Fade>
//                         ) : null}
//                     </DialogContent>

//                     {/* Actions */}
//                     <DialogActions sx={{ px: 3, py: 2, bgcolor: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
//                         <Button onClick={() => setEditSkillModal(false)} disabled={savingSkills} sx={{ color: "#64748b", fontSize: '0.8rem', fontWeight: 700, fontFamily: 'inherit', textTransform: 'none', px: 2 }}>Cancel</Button>
//                         <Button variant="contained" onClick={handleSaveSkills} disabled={savingSkills || skillsLoading} sx={{ borderRadius: '10px', fontSize: '0.8rem', textTransform: 'none', fontFamily: 'inherit', fontWeight: 700, minWidth: 120, px: 2, background: saveSuccess ? "#10b981" : "#0f172a", '&:hover': { background: saveSuccess ? "#059669" : "#334155" } }}>
//                             {savingSkills ? <span className="loader-ring" /> : saveSuccess ? <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} /> : "Save Configuration"}
//                         </Button>
//                     </DialogActions>
//                 </Dialog>
//             )}

//             {/* Global Snackbar */}
//             <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
//                 <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '10px', fontSize: '0.8rem', boxShadow: '0 8px 24px -8px rgba(0,0,0,0.15)', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
//                     {snackbar.message}
//                 </Alert>
//             </Snackbar>

//         </Box>
//     );
// };

// export default RecruiterLanding;
///////////////////////////////////////////Seperator?????????????////////////////////////////////////
// Claude  code
// import React, { useState, useEffect, useRef } from "react";

// import {
//     Box,
//     Typography,
//     Fade,
//     Tooltip,
//     Stack,
//     Chip,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     TextField,
//     Button,
//     Autocomplete,
//     CircularProgress,
//     Snackbar,
//     Alert,
//     InputAdornment,
//     Table,
//     TableBody,
//     TableCell,
//     TableContainer,
//     TableHead,
//     TableRow,
//     Paper,
//     IconButton,
// } from "@mui/material";

// import {
//     LocationOn as LocationIcon,
//     CalendarToday as CalendarIcon,
//     UploadFile as UploadFileIcon,
//     PsychologyAlt as PsychologyAltIcon,
//     CheckCircle as CheckCircleIcon,
//     Close as CloseIcon,
//     Add as AddIcon,
//     Search as SearchIcon,
//     AutoAwesome as AutoAwesomeIcon,
//     PeopleAlt as PeopleAltIcon,
//     ArrowOutward as ArrowOutwardIcon,
//     BusinessCenter as BusinessCenterIcon,
//     GridView as GridViewIcon,
//     ViewList as ViewListIcon,
//     Tune as TuneIcon,
//     RocketLaunch as RocketLaunchIcon,
//     Psychology as PsychologyIcon,
// } from "@mui/icons-material";

// import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// // Actual routing and API imports
// import { useNavigate } from "react-router-dom";
// import { useTheme, alpha } from "@mui/material/styles";
// import { jdApi } from "../../api/jobdescriptionApi";
// import { candidateApi } from "../../api/candidateApi";
// import { ERROR_MESSAGES, getPlanExhaustMessage } from "../../components/common/StatusDictionary";
// import FileUploadModal from "../FileUploadModal";

// /* ------------------------------------------------------------------
//    SKELETON GRID
// ------------------------------------------------------------------ */
// const SkeltonGrid = () => {
//     const theme = useTheme();
//     return (
//         <Box sx={{
//             display: 'grid',
//             gridTemplateColumns: {
//                 xs: '1fr',
//                 sm: 'repeat(2, 1fr)',
//                 md: 'repeat(3, 1fr)',
//                 xl: 'repeat(4, 1fr)'
//             },
//             gap: 2.2
//         }}>
//             {[1, 2, 3, 4, 5, 6].map((i) => (
//                 <Box
//                     key={i}
//                     sx={{
//                         height: 180,
//                         borderRadius: '16px',
//                         border: `1px solid ${theme.palette.divider}`,
//                         background: `linear-gradient(90deg, ${alpha(theme.palette.text.primary, 0.03)} 25%, ${alpha(theme.palette.text.primary, 0.08)} 50%, ${alpha(theme.palette.text.primary, 0.03)} 75%)`,
//                         backgroundSize: '1000px 100%',
//                         animation: 'shimmer 2s infinite linear',
//                         '@keyframes shimmer': {
//                             '0%': { backgroundPosition: '-1000px 0' },
//                             '100%': { backgroundPosition: '1000px 0' }
//                         }
//                     }}
//                 />
//             ))}
//         </Box>
//     );
// };

// /* ------------------------------------------------------------------
//    MAIN COMPONENT
// ------------------------------------------------------------------ */
// const RecruiterLanding = () => {
//     const navigate = useNavigate();
//     const theme = useTheme();
//     const isDark = theme.palette.mode === 'dark';
//     const hasFetchedRef = useRef(false);

//     // State
//     const [cardData, setCardData] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [searchQuery, setSearchQuery] = useState("");
//     const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

//     // Modals
//     const [showUploadModal, setShowUploadModal] = useState(false);
//     const [applyJob, setApplyJob] = useState(null);
//     const [editSkillModal, setEditSkillModal] = useState(false);

//     // Trackers
//     const [viewingJdId, setViewingJdId] = useState(null);
//     const [isSubmitting, setIsSubmitting] = useState(false);
//     const [selectedJD, setSelectedJD] = useState(null);
//     const [skillData, setSkillData] = useState(null);
//     const [skillsLoading, setSkillsLoading] = useState(false);
//     const [savingSkills, setSavingSkills] = useState(false);
//     const [openingSkillsId, setOpeningSkillsId] = useState(null);
//     const [saveSuccess, setSaveSuccess] = useState(false);

//     const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

//     // Initial Fetch
//     useEffect(() => {
//         if (!hasFetchedRef.current) {
//             hasFetchedRef.current = true;
//             fetchJDs();
//         }
//     }, []);

//     const fetchJDs = async () => {
//         setLoading(true);
//         try {
//             const response = await jdApi.getRecruiterJDs();
//             if (response?.status === 200) {
//                 setCardData(Array.isArray(response.data) ? response.data : []);
//             }
//         } catch (err) {
//             setSnackbar({ open: true, message: "Failed to fetch job descriptions.", severity: "error" });
//         } finally {
//             setLoading(false);
//         }
//     };

//     const viewJd = async (item) => {
//         if (viewingJdId) return;
//         setViewingJdId(item.id);
//         try {
//             const response = await jdApi.viewJD({ jd_id: item.id });
//             const url = response?.data?.download_url;
//             if (url) window.open(url, "_blank");
//         } catch (error) {
//             setSnackbar({ open: true, message: "Failed to open JD", severity: "error" });
//         } finally {
//             setViewingJdId(null);
//         }
//     };

//     const openSkillEditor = async (item) => {
//         setOpeningSkillsId(item.id);
//         setSelectedJD(item);
//         setSkillData(null);
//         setEditSkillModal(true);
//         setSkillsLoading(true);
//         setSaveSuccess(false);

//         try {
//             const response = await jdApi.getRecruiterJDDetail(item.id);
//             if (response?.status === 200) setSkillData(response.data);
//             else {
//                 setSnackbar({ open: true, message: "Failed to fetch JD details", severity: "error" });
//                 setEditSkillModal(false);
//             }
//         } catch (error) {
//             setSnackbar({ open: true, message: "Failed to load skills", severity: "error" });
//             setEditSkillModal(false);
//         } finally {
//             setSkillsLoading(false);
//             setOpeningSkillsId(null);
//         }
//     };

//     const handleSaveSkills = async () => {
//         try {
//             setSavingSkills(true);
//             setSaveSuccess(false);

//             const payload = {
//                 skill: {
//                     ...skillData.skill,
//                     keySkills: Array.isArray(skillData.skill.keySkills)
//                         ? skillData.skill.keySkills.join(", ")
//                         : skillData.skill.keySkills,
//                     additionalSkills: Array.isArray(skillData.skill.additionalSkills)
//                         ? skillData.skill.additionalSkills.join(", ")
//                         : skillData.skill.additionalSkills,
//                 },
//             };

//             const response = await jdApi.updateRecruiterJDDetail(selectedJD.id, payload);

//             if (response?.status === 200) {
//                 setSaveSuccess(true);
//                 setTimeout(() => {
//                     setSnackbar({ open: true, message: "Skills updated successfully", severity: "success" });
//                     fetchJDs();
//                     setEditSkillModal(false);
//                     setSaveSuccess(false);
//                 }, 800);
//             } else {
//                 setSnackbar({ open: true, message: response?.message || "Failed to update skills", severity: "error" });
//             }
//         } catch (error) {
//             setSnackbar({ open: true, message: "Failed to update skills", severity: "error" });
//         } finally {
//             setSavingSkills(false);
//         }
//     };

//     const handleUploadSuccess = async (data) => {
//         const id = data?.data?.data?.id;
//         if (id) {
//             navigate("/recruiter/analysis", { state: { id } });
//         } else {
//             setSnackbar({ open: true, message: "JD uploaded successfully!", severity: "success" });
//             fetchJDs();
//         }
//     };

//     const handleApplySuccess = () => {
//         setApplyJob(null);
//         setSnackbar({ open: true, message: "Resume uploaded successfully!", severity: "success" });
//         fetchJDs();
//     };

//     const getStatusStyle = (status) => {
//         switch (status?.toLowerCase()) {
//             case "active": return { bg: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main, border: alpha(theme.palette.success.main, 0.3) };
//             case "draft": return { bg: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main, border: alpha(theme.palette.warning.main, 0.3) };
//             case "closed": return { bg: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, border: alpha(theme.palette.error.main, 0.3) };
//             case "open": return { bg: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, border: alpha(theme.palette.info.main, 0.3) };
//             default: return { bg: alpha(theme.palette.text.secondary, 0.05), color: theme.palette.text.secondary, border: theme.palette.divider };
//         }
//     };

//     const filteredData = cardData.filter((item) =>
//         item.job_title?.toLowerCase().includes(searchQuery.toLowerCase())
//     );

//     const keySkillsValue = skillData
//         ? (Array.isArray(skillData?.skill?.keySkills)
//             ? skillData.skill.keySkills
//             : typeof skillData?.skill?.keySkills === "string"
//                 ? skillData.skill.keySkills.split(",").map((s) => s.trim()).filter(Boolean)
//                 : [])
//         : [];

//     // ─── SHARED BUTTON STYLES ────────────────────────────────────────────────
//     // All action buttons now use ONE consistent color family per action type:
//     //   Apply   → primary (blue)
//     //   Match   → indigo / secondary
//     //   Skills  → amber/warning
//     // View icon button stays neutral.

//     const applyBtnSx = {
//         flex: 1, height: 28, borderRadius: '8px',
//         bgcolor: alpha(theme.palette.primary.main, 0.1),
//         color: theme.palette.primary.main,
//         fontWeight: 700, fontSize: '0.68rem', textTransform: 'none', fontFamily: 'inherit',
//         border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
//         '&:hover': { bgcolor: theme.palette.primary.main, color: '#fff', borderColor: theme.palette.primary.main }
//     };

//     const matchBtnSx = (disabled) => ({
//         flex: 1, height: 28, borderRadius: '8px',
//         bgcolor: disabled ? alpha(theme.palette.text.primary, 0.05) : alpha(theme.palette.secondary.main, 0.1),
//         color: disabled ? theme.palette.text.disabled : theme.palette.secondary.main,
//         fontWeight: 700, fontSize: '0.68rem', textTransform: 'none', fontFamily: 'inherit',
//         border: `1px solid ${disabled ? 'transparent' : alpha(theme.palette.secondary.main, 0.2)}`,
//         '&:hover': disabled ? {} : { bgcolor: theme.palette.secondary.main, color: '#fff', borderColor: theme.palette.secondary.main },
//         cursor: disabled ? 'not-allowed' : 'pointer',
//         pointerEvents: disabled ? 'none' : 'auto',
//     });

//     const skillsBtnSx = (disabled) => ({
//         flex: 1, height: 28, borderRadius: '8px',
//         bgcolor: disabled ? alpha(theme.palette.text.primary, 0.05) : alpha(theme.palette.warning.main, 0.1),
//         color: disabled ? theme.palette.text.disabled : theme.palette.warning.dark,
//         fontWeight: 700, fontSize: '0.68rem', textTransform: 'none', fontFamily: 'inherit',
//         border: `1px solid ${disabled ? 'transparent' : alpha(theme.palette.warning.main, 0.25)}`,
//         '&:hover': disabled ? {} : { bgcolor: theme.palette.warning.main, color: '#fff', borderColor: theme.palette.warning.main },
//         cursor: disabled ? 'not-allowed' : 'pointer',
//         pointerEvents: disabled ? 'none' : 'auto',
//     });

//     // Tooltip shared props
//     const ttProps = {
//         arrow: true,
//         componentsProps: {
//             tooltip: {
//                 sx: {
//                     bgcolor: theme.palette.text.primary,
//                     color: theme.palette.background.paper,
//                     fontWeight: 500,
//                     fontSize: '0.65rem',
//                     fontFamily: "'Plus Jakarta Sans', sans-serif",
//                     p: '4px 8px',
//                     borderRadius: '6px',
//                 }
//             }
//         }
//     };

//     /* ----------------------------------------------------------------
//        CARD RENDER (Grid View)
//     ---------------------------------------------------------------- */
//     const renderCard = (item) => {
//         const statusStyle = getStatusStyle(item.status);
//         const hasApplicants = item.applications > 0;
//         const isViewingThis = viewingJdId === item.id;
//         const isOpeningSkills = openingSkillsId === item.id;

//         return (
//             <Box
//                 key={item.id}
//                 sx={{
//                     display: 'flex',
//                     flexDirection: 'column',
//                     bgcolor: theme.palette.background.paper,
//                     borderRadius: '16px',
//                     p: 2,
//                     border: `1px solid ${theme.palette.divider}`,
//                     boxShadow: isDark ? '0 8px 24px -10px rgba(0,0,0,0.5)' : '0 8px 24px -10px rgba(15, 23, 42, 0.05)',
//                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
//                     position: 'relative',
//                     overflow: 'hidden',
//                     '&:hover': {
//                         boxShadow: isDark ? '0 16px 32px -12px rgba(0,0,0,0.6)' : '0 16px 32px -12px rgba(15, 23, 42, 0.1)',
//                         transform: 'translateY(-3px)',
//                         borderColor: alpha(theme.palette.text.primary, 0.2)
//                     },
//                 }}
//             >
//                 {/* Decorative blob */}
//                 <Box sx={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`, borderRadius: '50%', pointerEvents: 'none' }} />

//                 <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

//                     {/* Card Header */}
//                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.8, gap: 1 }}>

//                         {/* Icon + Title */}
//                         <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', overflow: 'hidden' }}>
//                             <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: alpha(theme.palette.text.primary, 0.04), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
//                                 <BusinessCenterIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
//                             </Box>
//                             <Box sx={{ overflow: 'hidden', flex: 1 }}>
//                                 <Tooltip title={item.job_title} placement="top" {...ttProps}>
//                                     <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: 'default' }}>
//                                         {item.job_title}
//                                     </Typography>
//                                 </Tooltip>
//                             </Box>
//                         </Box>

//                         {/* View icon + Status */}
//                         <Stack direction="row" alignItems="center" spacing={0.6} sx={{ flexShrink: 0 }}>
//                             <Tooltip title="Open Job Description" placement="top" {...ttProps}>
//                                 <span>
//                                     <IconButton
//                                         size="small"
//                                         onClick={() => viewJd(item)}
//                                         disabled={!!viewingJdId}
//                                         sx={{ p: 0.3, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper, '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) } }}
//                                     >
//                                         {isViewingThis ? (
//                                             <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderTopColor: theme.palette.primary.main, animation: 'spin 0.8s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
//                                         ) : (
//                                             <ArrowOutwardIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
//                                         )}
//                                     </IconButton>
//                                 </span>
//                             </Tooltip>
//                             <Tooltip title={`Status: ${item.status}`} placement="top" {...ttProps}>
//                                 <Chip
//                                     label={item.status}
//                                     size="small"
//                                     sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`, fontWeight: 700, fontSize: "0.55rem", height: 20, borderRadius: "6px", px: 0.2 }}
//                                 />
//                             </Tooltip>
//                         </Stack>
//                     </Box>

//                     {/* Meta */}
//                     <Stack spacing={0.8} mb={2.5}>
//                         <Tooltip title="Job location" placement="left" {...ttProps}>
//                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, width: 'fit-content' }}>
//                                 <LocationIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
//                                 <Typography sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary, fontWeight: 500 }} noWrap>
//                                     {item.location || "Not Specified"}
//                                 </Typography>
//                             </Box>
//                         </Tooltip>
//                         <Tooltip title="Date created" placement="left" {...ttProps}>
//                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, width: 'fit-content' }}>
//                                 <CalendarIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
//                                 <Typography sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary, fontWeight: 500 }}>
//                                     Created {item.created_at ? new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A"}
//                                 </Typography>
//                             </Box>
//                         </Tooltip>
//                         <Tooltip title="Total applications received" placement="left" {...ttProps}>
//                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, width: 'fit-content' }}>
//                                 <PeopleAltIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
//                                 <Typography sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary, fontWeight: 600 }}>
//                                     {item.applications || 0} Applicants pending
//                                 </Typography>
//                             </Box>
//                         </Tooltip>
//                     </Stack>
//                 </Box>

//                 {/* Card Footer — unified button palette */}
//                 <Box sx={{ mt: 'auto', display: 'flex', gap: 0.8 }}>

//                     <Tooltip title="Upload a resume to apply for this position" placement="top" {...ttProps}>
//                         <Button onClick={() => setApplyJob(item)} sx={applyBtnSx}>
//                             Apply
//                         </Button>
//                     </Tooltip>

//                     <Tooltip title={hasApplicants ? "Run AI matching for applicants" : "No applicants yet — matching unavailable"} placement="top" {...ttProps}>
//                         <span style={{ flex: 1 }}>
//                             <Button
//                                 disabled={!hasApplicants}
//                                 onClick={() => navigate("/recruiter/llm-matching", { state: { jdId: item.id } })}
//                                 sx={{ ...matchBtnSx(!hasApplicants), flex: 'none', width: '100%' }}
//                             >
//                                 Match
//                             </Button>
//                         </span>
//                     </Tooltip>

//                     <Tooltip title={hasApplicants ? "Skills can't be edited after applications arrive" : "Edit required skills for evaluation"} placement="top" {...ttProps}>
//                         <span style={{ flex: 1 }}>
//                             <Button
//                                 disabled={item.applications > 0 || isOpeningSkills}
//                                 onClick={() => openSkillEditor(item)}
//                                 sx={{ ...skillsBtnSx(item.applications > 0), flex: 'none', width: '100%' }}
//                             >
//                                 {isOpeningSkills ? (
//                                     <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderTopColor: theme.palette.warning.main, animation: 'spin 0.8s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
//                                 ) : "Skills"}
//                             </Button>
//                         </span>
//                     </Tooltip>
//                 </Box>
//             </Box>
//         );
//     };

//     /* ----------------------------------------------------------------
//        LIST ROW RENDER (Table View)
//     ---------------------------------------------------------------- */
//     const renderListView = () => (
//         <TableContainer component={Paper} sx={{ borderRadius: '16px', border: `1px solid ${theme.palette.divider}`, boxShadow: isDark ? '0 8px 24px -10px rgba(0,0,0,0.4)' : '0 4px 16px -8px rgba(15,23,42,0.06)', bgcolor: theme.palette.background.paper, overflow: 'hidden' }}>
//             <Table size="small">
//                 <TableHead>
//                     <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03), '& .MuiTableCell-head': { fontSize: '0.65rem', fontWeight: 800, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${theme.palette.divider}`, py: 1.4, fontFamily: "'Plus Jakarta Sans', sans-serif" } }}>
//                         <TableCell>Job Title</TableCell>
//                         <TableCell>Location</TableCell>
//                         <TableCell>Status</TableCell>
//                         <TableCell>Created</TableCell>
//                         <TableCell align="center">Applicants</TableCell>
//                         <TableCell align="right" sx={{ pr: 2 }}>Actions</TableCell>
//                     </TableRow>
//                 </TableHead>
//                 <TableBody>
//                     {filteredData.map((item, idx) => {
//                         const statusStyle = getStatusStyle(item.status);
//                         const hasApplicants = item.applications > 0;
//                         const isViewingThis = viewingJdId === item.id;
//                         const isOpeningSkills = openingSkillsId === item.id;

//                         return (
//                             <TableRow
//                                 key={item.id}
//                                 sx={{
//                                     bgcolor: idx % 2 === 0 ? 'transparent' : alpha(theme.palette.text.primary, 0.015),
//                                     transition: 'background 0.15s',
//                                     '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
//                                     '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`, py: 1.2, fontFamily: "'Plus Jakarta Sans', sans-serif" }
//                                 }}
//                             >
//                                 {/* Title */}
//                                 <TableCell>
//                                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                         <Box sx={{ width: 26, height: 26, borderRadius: '7px', bgcolor: alpha(theme.palette.text.primary, 0.05), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
//                                             <BusinessCenterIcon sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
//                                         </Box>
//                                         <Tooltip title={item.job_title} placement="top" {...ttProps}>
//                                             <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: theme.palette.text.primary, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}>
//                                                 {item.job_title}
//                                             </Typography>
//                                         </Tooltip>
//                                     </Box>
//                                 </TableCell>

//                                 {/* Location */}
//                                 <TableCell>
//                                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
//                                         <LocationIcon sx={{ fontSize: 11, color: theme.palette.text.secondary }} />
//                                         <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary, fontWeight: 500 }}>
//                                             {item.location || "—"}
//                                         </Typography>
//                                     </Box>
//                                 </TableCell>

//                                 {/* Status */}
//                                 <TableCell>
//                                     <Tooltip title={`Posting status: ${item.status}`} placement="top" {...ttProps}>
//                                         <Chip label={item.status} size="small" sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`, fontWeight: 700, fontSize: "0.58rem", height: 20, borderRadius: "6px" }} />
//                                     </Tooltip>
//                                 </TableCell>

//                                 {/* Created */}
//                                 <TableCell>
//                                     <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.secondary, fontWeight: 500 }}>
//                                         {item.created_at ? new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A"}
//                                     </Typography>
//                                 </TableCell>

//                                 {/* Applicants */}
//                                 <TableCell align="center">
//                                     <Tooltip title="Applications received" placement="top" {...ttProps}>
//                                         <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
//                                             <PeopleAltIcon sx={{ fontSize: 11, color: item.applications > 0 ? theme.palette.primary.main : theme.palette.text.secondary }} />
//                                             <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: item.applications > 0 ? theme.palette.primary.main : theme.palette.text.secondary }}>
//                                                 {item.applications || 0}
//                                             </Typography>
//                                         </Box>
//                                     </Tooltip>
//                                 </TableCell>

//                                 {/* Actions */}
//                                 <TableCell align="right">
//                                     <Stack direction="row" spacing={0.7} justifyContent="flex-end">

//                                         {/* View JD */}
//                                         <Tooltip title="Open Job Description PDF" placement="top" {...ttProps}>
//                                             <span>
//                                                 <IconButton
//                                                     size="small"
//                                                     onClick={() => viewJd(item)}
//                                                     disabled={!!viewingJdId}
//                                                     sx={{ p: 0.5, border: `1px solid ${theme.palette.divider}`, borderRadius: '7px', bgcolor: theme.palette.background.paper, '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.05) } }}
//                                                 >
//                                                     {isViewingThis ? (
//                                                         <Box sx={{ width: 11, height: 11, borderRadius: '50%', border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderTopColor: theme.palette.primary.main, animation: 'spin 0.8s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
//                                                     ) : (
//                                                         <ArrowOutwardIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
//                                                     )}
//                                                 </IconButton>
//                                             </span>
//                                         </Tooltip>

//                                         {/* Apply */}
//                                         <Tooltip title="Upload a resume for this role" placement="top" {...ttProps}>
//                                             <Button onClick={() => setApplyJob(item)} sx={{ ...applyBtnSx, flex: 'none', height: 26, px: 1.2, fontSize: '0.65rem' }}>
//                                                 Apply
//                                             </Button>
//                                         </Tooltip>

//                                         {/* Match */}
//                                         <Tooltip title={hasApplicants ? "Run AI matching" : "No applicants yet"} placement="top" {...ttProps}>
//                                             <span>
//                                                 <Button
//                                                     disabled={!hasApplicants}
//                                                     onClick={() => navigate("/recruiter/llm-matching", { state: { jdId: item.id } })}
//                                                     sx={{ ...matchBtnSx(!hasApplicants), flex: 'none', height: 26, px: 1.2, fontSize: '0.65rem' }}
//                                                 >
//                                                     Match
//                                                 </Button>
//                                             </span>
//                                         </Tooltip>

//                                         {/* Skills */}
//                                         <Tooltip title={hasApplicants ? "Locked after applications received" : "Edit required skills"} placement="top" {...ttProps}>
//                                             <span>
//                                                 <Button
//                                                     disabled={item.applications > 0 || isOpeningSkills}
//                                                     onClick={() => openSkillEditor(item)}
//                                                     sx={{ ...skillsBtnSx(item.applications > 0), flex: 'none', height: 26, px: 1.2, fontSize: '0.65rem' }}
//                                                 >
//                                                     {isOpeningSkills ? (
//                                                         <Box sx={{ width: 11, height: 11, borderRadius: '50%', border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderTopColor: theme.palette.warning.main, animation: 'spin 0.8s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
//                                                     ) : "Skills"}
//                                                 </Button>
//                                             </span>
//                                         </Tooltip>
//                                     </Stack>
//                                 </TableCell>
//                             </TableRow>
//                         );
//                     })}
//                 </TableBody>
//             </Table>
//         </TableContainer>
//     );

//     /* ----------------------------------------------------------------
//        RENDER
//     ---------------------------------------------------------------- */
//     return (
//         <Box sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default, p: { xs: 1.2, md: 2.5, lg: 3.5 }, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

//             {/* 1. Header & Controls */}
//             <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.8, mb: 3.5 }}>

//                 {/* Title */}
//                 <Box>
//                     <Typography variant="h4" sx={{ fontSize: '1.55rem', fontWeight: 800, color: theme.palette.text.primary, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 1.2 }}>
//                         Workspace
//                         <Tooltip title={`${cardData.length} active job openings`} placement="right" {...ttProps}>
//                             <Chip label={`${cardData.length} Openings`} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontWeight: 700, borderRadius: '6px', fontSize: '0.6rem', height: 20, cursor: 'default' }} />
//                         </Tooltip>
//                     </Typography>
//                     <Typography variant="body2" sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary, mt: 0.5, fontWeight: 500 }}>
//                         Manage your job postings and track applicants seamlessly.
//                     </Typography>
//                 </Box>

//                 {/* Controls */}
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>

//                     {/* Search */}
//                     <Tooltip title="Search by job title" placement="bottom" {...ttProps}>
//                         <TextField
//                             value={searchQuery}
//                             onChange={(e) => setSearchQuery(e.target.value)}
//                             placeholder="Search positions..."
//                             InputProps={{
//                                 startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} /></InputAdornment>,
//                             }}
//                             sx={{
//                                 minWidth: { xs: '100%', sm: 220 },
//                                 '& .MuiOutlinedInput-root': {
//                                     height: 34,
//                                     bgcolor: theme.palette.background.paper,
//                                     borderRadius: '8px',
//                                     fontSize: '0.72rem',
//                                     fontWeight: 500,
//                                     fontFamily: "'Plus Jakarta Sans', sans-serif",
//                                     transition: 'all 0.2s ease',
//                                     boxShadow: isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.02)',
//                                     '& fieldset': { borderColor: theme.palette.divider, borderWidth: '1px' },
//                                     '&:hover fieldset': { borderColor: alpha(theme.palette.text.primary, 0.3) },
//                                     '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: '2px' },
//                                     '&.Mui-focused': { boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}` }
//                                 }
//                             }}
//                         />
//                     </Tooltip>

//                     {/* View Toggle */}
//                     <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', overflow: 'hidden', bgcolor: theme.palette.background.paper, boxShadow: isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.02)' }}>
//                         <Tooltip title="Grid view" placement="bottom" {...ttProps}>
//                             <IconButton
//                                 size="small"
//                                 onClick={() => setViewMode("grid")}
//                                 sx={{
//                                     borderRadius: 0, height: 34, width: 34,
//                                     bgcolor: viewMode === 'grid' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
//                                     color: viewMode === 'grid' ? theme.palette.primary.main : theme.palette.text.secondary,
//                                     '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
//                                 }}
//                             >
//                                 <GridViewIcon sx={{ fontSize: 16 }} />
//                             </IconButton>
//                         </Tooltip>
//                         <Box sx={{ width: '1px', height: 20, bgcolor: theme.palette.divider }} />
//                         <Tooltip title="List view" placement="bottom" {...ttProps}>
//                             <IconButton
//                                 size="small"
//                                 onClick={() => setViewMode("list")}
//                                 sx={{
//                                     borderRadius: 0, height: 34, width: 34,
//                                     bgcolor: viewMode === 'list' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
//                                     color: viewMode === 'list' ? theme.palette.primary.main : theme.palette.text.secondary,
//                                     '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
//                                 }}
//                             >
//                                 <ViewListIcon sx={{ fontSize: 16 }} />
//                             </IconButton>
//                         </Tooltip>
//                     </Box>

//                     {/* Upload */}
//                     <Tooltip title="Upload a job description PDF or DOCX" placement="bottom" {...ttProps}>
//                         <Button
//                             onClick={() => setShowUploadModal(true)}
//                             startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
//                             sx={{
//                                 height: 34, px: 2, borderRadius: '8px', flex: { xs: 1, sm: 'none' },
//                                 textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', fontFamily: 'inherit',
//                                 bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, border: `1px solid ${theme.palette.divider}`,
//                                 boxShadow: isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.02)',
//                                 transition: 'all 0.2s',
//                                 '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04), borderColor: alpha(theme.palette.text.primary, 0.3), transform: 'translateY(-1px)' }
//                             }}
//                         >
//                             Upload
//                         </Button>
//                     </Tooltip>

//                     {/* Generate */}
//                     <Tooltip title="Use AI to auto-generate a Job Description" placement="bottom" {...ttProps}>
//                         <Button
//                             onClick={() => navigate("/recruiter/jd-generator")}
//                             startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
//                             sx={{
//                                 height: 34, px: 2, borderRadius: '8px', flex: { xs: 1, sm: 'none' },
//                                 textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', fontFamily: 'inherit',
//                                 background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`, color: '#fff',
//                                 boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
//                                 transition: 'all 0.2s',
//                                 '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}` }
//                             }}
//                         >
//                             Generate
//                         </Button>
//                     </Tooltip>
//                 </Box>
//             </Box>

//             {/* 2. Grid / List Area */}
//             <Box>
//                 {loading ? (
//                     <SkeltonGrid />
//                 ) : (
//                     <Fade in={!loading} timeout={500}>
//                         <Box>
//                             {filteredData.length > 0 ? (
//                                 viewMode === 'grid' ? (
//                                     <Box sx={{
//                                         display: 'grid',
//                                         gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
//                                         gap: 2.2
//                                     }}>
//                                         {filteredData.map(renderCard)}
//                                     </Box>
//                                 ) : (
//                                     renderListView()
//                                 )
//                             ) : (
//                                 /* Empty State */
//                                 <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: "center", borderRadius: '16px', border: `2px dashed ${theme.palette.divider}`, py: 5, bgcolor: "transparent" }}>
//                                     <Box sx={{ width: 56, height: 56, borderRadius: '12px', bgcolor: alpha(theme.palette.text.primary, 0.04), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
//                                         <BusinessCenterIcon sx={{ fontSize: 24, color: theme.palette.text.secondary }} />
//                                     </Box>
//                                     <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.8, fontWeight: 800, color: theme.palette.text.primary, fontFamily: 'inherit' }}>
//                                         No Openings Found
//                                     </Typography>
//                                     <Typography variant="body2" color={theme.palette.text.secondary} sx={{ fontSize: '0.72rem', mb: 3, fontWeight: 500, fontFamily: 'inherit' }}>
//                                         {searchQuery ? "Try adjusting your search criteria." : "Get started by uploading your first Job Description."}
//                                     </Typography>
//                                     {!searchQuery && (
//                                         <Button
//                                             variant="contained"
//                                             onClick={() => setShowUploadModal(true)}
//                                             startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
//                                             sx={{ borderRadius: '8px', fontSize: '0.72rem', textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', px: 2, py: 0.8 }}
//                                         >
//                                             Upload First JD
//                                         </Button>
//                                     )}
//                                 </Box>
//                             )}
//                         </Box>
//                     </Fade>
//                 )}
//             </Box>

//             {/* 3. Modals & Notifications */}

//             {applyJob && (
//                 <FileUploadModal
//                     open={Boolean(applyJob)}
//                     uploadType="resume"
//                     apiEndpoint={candidateApi.apply}
//                     onClose={() => setApplyJob(null)}
//                     setLoading={setIsSubmitting}
//                     extraPayload={{ job_id: applyJob.id, recruiter_id: applyJob.created_by }}
//                     postUploadHandler={() => handleApplySuccess()}
//                     onError={(error) => {
//                         const errorMessage = error?.planExhaust
//                             ? getPlanExhaustMessage(error.message)
//                             : error?.isRawMessage
//                                 ? error.message
//                                 : ERROR_MESSAGES[error?.message] || ERROR_MESSAGES.UnexpectedError || "Application failed.";
//                         setSnackbar({ open: true, message: errorMessage, severity: error?.severity || "error" });
//                     }}
//                 />
//             )}

//             {showUploadModal && (
//                 <FileUploadModal
//                     open={showUploadModal}
//                     uploadType="jd"
//                     apiEndpoint={jdApi.createRecruiterJD}
//                     onClose={() => setShowUploadModal(false)}
//                     setLoading={setLoading}
//                     onSuccess={(data) => { handleUploadSuccess(data); }}
//                     onError={(error) => {
//                         const errorMessage = error?.planExhaust
//                             ? getPlanExhaustMessage(error.message)
//                             : error?.isRawMessage
//                                 ? error.message
//                                 : ERROR_MESSAGES[error?.message] || ERROR_MESSAGES.UnexpectedError || "JD upload failed.";
//                         setSnackbar({ open: true, message: errorMessage, severity: error?.severity || "error" });
//                     }}
//                 />
//             )}

//             {/* Skills Modal */}
//             {editSkillModal && (
//                 <Dialog
//                     open={editSkillModal}
//                     onClose={() => { if (!savingSkills) setEditSkillModal(false); }}
//                     maxWidth="sm"
//                     fullWidth
//                     PaperProps={{
//                         sx: {
//                             borderRadius: "16px",
//                             overflow: "hidden",
//                             fontFamily: "'Plus Jakarta Sans', sans-serif",
//                             boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.6)' : '0 20px 40px -10px rgba(0, 0, 0, 0.2)',
//                             bgcolor: theme.palette.background.paper
//                         }
//                     }}
//                 >
//                     {/* Header */}
//                     <Box sx={{ background: isDark ? theme.palette.background.default : `linear-gradient(135deg, ${theme.palette.grey[800]} 0%, ${theme.palette.grey[900]} 100%)`, px: 2.5, pt: 2.5, pb: 1.8, position: "relative", overflow: 'hidden' }}>
//                         <Box sx={{ position: 'absolute', top: -20, right: -10, width: 70, height: 70, background: `radial-gradient(circle, ${alpha(theme.palette.warning.main, 0.3)} 0%, transparent 70%)`, borderRadius: '50%' }} />

//                         <Tooltip title="Close" placement="left" {...ttProps}>
//                             <IconButton size="small" onClick={() => { if (!savingSkills) setEditSkillModal(false); }} disabled={savingSkills} sx={{ position: "absolute", top: 10, right: 10, color: alpha('#fff', 0.6), "&:hover": { color: "#fff", bgcolor: alpha('#fff', 0.1) } }}>
//                                 <CloseIcon sx={{ fontSize: 16 }} />
//                             </IconButton>
//                         </Tooltip>

//                         <Stack direction="row" alignItems="center" spacing={1.5}>
//                             <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: alpha('#fff', 0.1), border: `1px solid ${alpha('#fff', 0.1)}`, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: 'blur(10px)' }}>
//                                 <PsychologyAltIcon sx={{ fontSize: 20, color: theme.palette.mode === 'dark' ? theme.palette.warning.light : "#fbbf24" }} />
//                             </Box>
//                             <Box>
//                                 <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#fff", lineHeight: 1.2, fontFamily: 'inherit' }}>Required Skills Setup</Typography>
//                                 <Typography sx={{ fontSize: "0.68rem", color: alpha('#fff', 0.6), mt: 0.3, fontWeight: 500, fontFamily: 'inherit' }}>{selectedJD?.job_title || "Job Role"}</Typography>
//                             </Box>
//                         </Stack>
//                     </Box>

//                     {/* Content */}
//                     <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 2, bgcolor: theme.palette.background.paper }}>
//                         {skillsLoading ? (
//                             <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: theme.palette.warning.main }} /></Box>
//                         ) : skillData ? (
//                             <Fade in timeout={350}>
//                                 <Box>
//                                     <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, bgcolor: alpha(theme.palette.warning.main, 0.06), border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`, borderRadius: '10px', px: 1.8, py: 1.2, mb: 2.5 }}>
//                                         <AutoAwesomeIcon sx={{ fontSize: 14, color: theme.palette.warning.main, mt: 0.1 }} />
//                                         <Typography sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary, fontWeight: 500, fontFamily: 'inherit', lineHeight: 1.5 }}>
//                                             Type a core competency and press <strong>Enter</strong>. These skills will be used by the LLM engine during candidate evaluation.
//                                         </Typography>
//                                     </Box>

//                                     <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: theme.palette.text.primary, mb: 1.2, fontFamily: 'inherit' }}>Mandatory Evaluation Skills</Typography>
//                                     <Autocomplete
//                                         multiple
//                                         freeSolo
//                                         options={[]}
//                                         value={keySkillsValue}
//                                         onChange={(e, value) => setSkillData({ ...skillData, skill: { ...skillData.skill, keySkills: value } })}
//                                         renderInput={(params) => (
//                                             <TextField
//                                                 {...params}
//                                                 placeholder="e.g. React, Node.js, SQL…"
//                                                 variant="outlined"
//                                                 sx={{
//                                                     '& .MuiOutlinedInput-root': {
//                                                         borderRadius: '10px',
//                                                         bgcolor: theme.palette.background.default,
//                                                         fontSize: '0.78rem',
//                                                         '& fieldset': { borderColor: theme.palette.divider },
//                                                         '&:hover fieldset': { borderColor: alpha(theme.palette.warning.main, 0.5) },
//                                                         '&.Mui-focused fieldset': { borderColor: theme.palette.warning.main, borderWidth: '2px' }
//                                                     }
//                                                 }}
//                                             />
//                                         )}
//                                         renderTags={(value, getTagProps) =>
//                                             value.map((option, index) => (
//                                                 <Chip key={index} label={option} size="small" sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.dark, fontWeight: 700, fontSize: '0.65rem', fontFamily: 'inherit', borderRadius: '6px', m: 0.3, border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}` }} {...getTagProps({ index })} />
//                                             ))
//                                         }
//                                     />
//                                 </Box>
//                             </Fade>
//                         ) : null}
//                     </DialogContent>

//                     {/* Actions */}
//                     <DialogActions sx={{ px: 2.5, py: 1.8, bgcolor: theme.palette.background.default, borderTop: `1px solid ${theme.palette.divider}` }}>
//                         <Button onClick={() => setEditSkillModal(false)} disabled={savingSkills} sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', fontWeight: 700, fontFamily: 'inherit', textTransform: 'none', px: 1.5 }}>
//                             Cancel
//                         </Button>
//                         <Tooltip title={skillsLoading ? "Loading skills…" : "Save your skill configuration"} placement="top" {...ttProps}>
//                             <span>
//                                 <Button
//                                     variant="contained"
//                                     onClick={handleSaveSkills}
//                                     disabled={savingSkills || skillsLoading}
//                                     sx={{
//                                         borderRadius: '8px', fontSize: '0.72rem', textTransform: 'none', fontFamily: 'inherit', fontWeight: 700, minWidth: 110, px: 1.8,
//                                         background: saveSuccess ? theme.palette.success.main : theme.palette.warning.main,
//                                         '&:hover': { background: saveSuccess ? theme.palette.success.dark : theme.palette.warning.dark }
//                                     }}
//                                 >
//                                     {savingSkills
//                                         ? <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${alpha('#fff', 0.3)}`, borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
//                                         : saveSuccess
//                                             ? <><CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} /> Saved!</>
//                                             : "Save Config"
//                                     }
//                                 </Button>
//                             </span>
//                         </Tooltip>
//                     </DialogActions>
//                 </Dialog>
//             )}

//             {/* Global Snackbar */}
//             <Snackbar
//                 open={snackbar.open}
//                 autoHideDuration={4000}
//                 onClose={() => setSnackbar({ ...snackbar, open: false })}
//                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
//             >
//                 <Alert
//                     onClose={() => setSnackbar({ ...snackbar, open: false })}
//                     severity={snackbar.severity}
//                     sx={{ width: '100%', borderRadius: '8px', fontSize: '0.72rem', boxShadow: '0 8px 24px -8px rgba(0,0,0,0.15)', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
//                 >
//                     {snackbar.message}
//                 </Alert>
//             </Snackbar>

//         </Box>
//     );
// };

// export default RecruiterLanding;